/**
 * CandidateDetail — deep drill-in view with tabs.
 * Overview · Stage · Timeline · Notes · Comms · Documents · Applications
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TasksSection } from "./TasksSection";
import {
  ArrowLeft, Mail, Phone, Linkedin, ExternalLink, Trash2, Plus, X,
  Clock, StickyNote, MessageSquare, FileText, Briefcase, Sparkles,
  Star, Save, Download, CalendarX, CheckCircle2, Eye, GraduationCap, Pencil,
  ChevronDown, ChevronRight, Paperclip, RefreshCw, ArrowDownLeft, ArrowUpRight, Send, BarChart3,
} from "lucide-react";
import {
  BASE, CANDIDATE_STAGES, COACHING_STAGES, COMMS_CHANNELS, DOC_KINDS,
  StagePill, SectionHeader, EmptyState, MiniStat,
  cx, empty, relTime, channelMeta, authHeader,
} from "./shared";
import { CandidateSubmissions } from "./Submissions";
import { CandidateShortlistActivityPanel } from "@/components/shortlist/ShortlistActivityPanel";
import { CandidateEmailDialog } from "./CandidateEmailDialog";
import { FullHistoryPanel, ParsedCvPanel } from "./CandidateIngestSections";

// Coerce any backend value (array/object/null/number) to a plain string so
// form fields never crash on .trim() for legacy/imported shapes.
const toStr = (v) => {
  if (v === undefined || v === null) return "";
  if (Array.isArray(v))
    return v
      .map((x) => (x && typeof x === "object" ? Object.values(x).filter(Boolean).join(" — ") : x))
      .filter((x) => x != null && String(x).trim())
      .join("\n");
  if (typeof v === "object") return Object.values(v).filter(Boolean).join(", ");
  return String(v);
};

const TABS = [
  { k: "overview",     label: "Overview",     Icon: Sparkles },
  { k: "parsed_cv",    label: "Parsed CV",    Icon: FileText },
  { k: "full_history", label: "Full History", Icon: Clock },
  { k: "recruiter",    label: "Recruiter",    Icon: StickyNote },
  { k: "branded",      label: "Branded Profile", Icon: FileText },
  { k: "conversations", label: "Conversations", Icon: Mail },
  { k: "timeline",     label: "Timeline",     Icon: Clock },
  { k: "notes",        label: "Notes",        Icon: StickyNote },
  { k: "comms",        label: "Comms",        Icon: MessageSquare },
  { k: "documents",    label: "Documents",    Icon: FileText },
  { k: "applications", label: "Applications", Icon: Briefcase },
  { k: "submitted",    label: "Submitted to", Icon: Send },
  { k: "shortlist_activity", label: "Shortlist Activity", Icon: BarChart3 },
];

const GMAIL_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin/gmail`;

export default function CandidateDetail({ candidateId, pw, onBack, onRequestEdit, currentUser, reloadKey = 0 }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [timeline, setTimeline] = useState([]);
  const [notes, setNotes] = useState([]);
  const [comms, setComms] = useState([]);
  const [applications, setApplications] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);

  const H = useMemo(() => authHeader(pw), [pw]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/candidates/${candidateId}`, H);
      setRecord(r.data);
    } catch {
      toast.error("Couldn't load candidate.");
    } finally {
      setLoading(false);
    }
  }, [candidateId, H]);

  const loadTimeline = useCallback(async () => {
    const r = await axios.get(`${BASE}/timeline?entity_type=candidate&entity_id=${candidateId}`, H);
    setTimeline(r.data.items || []);
  }, [candidateId, H]);
  const loadNotes = useCallback(async () => {
    const r = await axios.get(`${BASE}/notes?entity_type=candidate&entity_id=${candidateId}`, H);
    setNotes(r.data.notes || []);
  }, [candidateId, H]);
  const loadComms = useCallback(async () => {
    const r = await axios.get(`${BASE}/comms?entity_type=candidate&entity_id=${candidateId}`, H);
    setComms(r.data.comms || []);
  }, [candidateId, H]);
  const loadApps = useCallback(async () => {
    // Single request → returns each application with its role + company joined.
    const r = await axios.get(`${BASE}/applications?candidate_id=${candidateId}`, H);
    // Group by role for the panel (multiple apps per role shouldn't happen, but keep the shape).
    const byRole = new Map();
    for (const a of r.data.applications || []) {
      if (!a.role) continue;
      const key = a.role.id;
      if (!byRole.has(key)) {
        byRole.set(key, { role: a.role, company: a.role.company, apps: [] });
      }
      byRole.get(key).apps.push(a);
    }
    setApplications(Array.from(byRole.values()));
  }, [candidateId, H]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (reloadKey) load(); }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!record) return;
    if (tab === "timeline") loadTimeline();
    if (tab === "notes")    loadNotes();
    if (tab === "comms")    loadComms();
    if (tab === "applications") loadApps();
  }, [tab, record, loadTimeline, loadNotes, loadComms, loadApps]);

  const setStage = async (stage) => {
    try {
      await axios.patch(`${BASE}/candidates/${candidateId}/stage`, { stage, reason: "" }, H);
      toast.success("Stage updated.");
      load();
      if (tab === "timeline") loadTimeline();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update stage.");
    }
  };

  if (loading || !record) {
    return <p className="text-center text-[#6F8FA8] py-20">Loading candidate…</p>;
  }

  const { candidate: c, person: p } = record;

  return (
    <div data-testid="candidate-detail" className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-[#E4E7EA] overflow-hidden">
        <div className="p-6 border-b border-[#F2F2EF]">
          <div className="flex items-center justify-between mb-4">
            <button
              data-testid="candidate-detail-back"
              onClick={onBack}
              className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] hover:text-[#0D2F4D] inline-flex items-center gap-1"
            >
              <ArrowLeft size={12} /> Back to candidates
            </button>
            <button
              data-testid="candidate-delete-profile"
              onClick={async () => {
                if (!p?.email) return;
                const purgePerson = window.confirm(
                  `DELETE candidate profile for ${p.first_name} ${p.last_name} (${p.email})?\n\n` +
                  `This removes:\n· Recruiter notes + references\n· Timeline history\n· Notes + call/meeting logs\n· Applications + documents\n\n` +
                  `Click OK to also remove the underlying person record (if not used elsewhere).\nClick Cancel to keep the person and only delete the candidate row.`
                );
                const confirmed = window.confirm("Are you absolutely sure? This can't be undone.");
                if (!confirmed) return;
                try {
                  await axios.delete(`${BASE}/candidates/${candidateId}?purge_person=${purgePerson ? "true" : "false"}`, authHeader(pw));
                  toast.success("Candidate profile deleted.");
                  onBack?.();
                } catch (err) {
                  toast.error(`Delete failed: ${err?.response?.data?.detail || err.message}`);
                }
              }}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-red-600 hover:text-white hover:bg-red-600 border border-red-200 rounded-full px-3 py-1.5 transition-colors"
            >
              <Trash2 size={12}/> Delete profile
            </button>
          </div>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex-1">
              <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0D2F4D] leading-tight">
                {p?.first_name || "Unknown"} {p?.last_name || ""}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-[#6F8FA8]">
                {p?.email && (
                  <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 hover:text-[#0D2F4D]">
                    <Mail size={13} /> {p.email}
                  </a>
                )}
                {p?.phone && (
                  <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 hover:text-[#0D2F4D]">
                    <Phone size={13} /> {p.phone}
                  </a>
                )}
                {c.linkedin && (
                  <a href={c.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#0D2F4D]">
                    <Linkedin size={13} /> LinkedIn
                  </a>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(p?.tags || []).map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#F2F2EF] text-[#0D2F4D] border border-[#E4E7EA]">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <button data-testid="candidate-send-branded-email" onClick={() => setSendOpen(true)} className="text-sm font-bold px-4 py-2 rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90 inline-flex items-center gap-1.5"><Send size={14}/>Send Email</button>
              <button
                data-testid="candidate-detail-branded-cv-pdf"
                onClick={async () => {
                  try {
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/candidates/${candidateId}/branded-cv.pdf`;
                    toast.loading("Rendering PDF…", { id: "cv-pdf" });
                    const r = await fetch(url, { headers: { "X-Admin-Password": pw } });
                    if (!r.ok) {
                      let msg = `HTTP ${r.status}`;
                      try { const j = await r.json(); if (j?.detail) msg = j.detail; } catch (_) {}
                      throw new Error(msg);
                    }
                    const blob = await r.blob();
                    const objUrl = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = objUrl;
                    const cd = r.headers.get("content-disposition") || "";
                    const m = cd.match(/filename="([^"]+)"/);
                    a.download = m ? m[1] : `ditch-careers-cv-${candidateId.slice(0,8)}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
                    toast.success("PDF downloaded", { id: "cv-pdf" });
                  } catch (e) { toast.error(`PDF failed: ${e.message}`, { id: "cv-pdf" }); }
                }}
                className="text-sm font-bold px-4 py-2 rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#d8e34a] inline-flex items-center gap-1.5"
                title="Server-renders a real PDF via reportlab — no print dialog needed"
              >
                <Download size={14}/>PDF
              </button>
              <button
                data-testid="candidate-detail-branded-cv"
                onClick={async () => {
                  // Fetch FIRST in the current tab (where the admin auth
                  // header travels reliably), THEN open a new tab with the
                  // resulting HTML as a blob URL. The old "open popup then
                  // fetch then write" approach failed on Safari (WebKit's
                  // fetch reports "Load failed" when the popup is written to
                  // before the fetch resolves).
                  const toastId = "cv-html";
                  try {
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/candidates/${candidateId}/branded-cv`;
                    toast.loading("Loading profile…", { id: toastId });
                    const r = await fetch(url, { headers: { "X-Admin-Password": pw } });
                    if (!r.ok) {
                      let msg = `HTTP ${r.status}`;
                      try { const j = await r.json(); if (j?.detail) msg = j.detail; } catch (_) {}
                      throw new Error(msg);
                    }
                    const html = await r.text();
                    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
                    const objUrl = URL.createObjectURL(blob);
                    const win = window.open(objUrl, "_blank");
                    if (!win) {
                      // Popup blocked — fall back to an in-place download.
                      const a = document.createElement("a");
                      a.href = objUrl;
                      a.download = `ditch-careers-candidate-profile-${candidateId.slice(0,8)}.html`;
                      document.body.appendChild(a); a.click(); a.remove();
                      toast.success("Profile downloaded (popup was blocked)", { id: toastId });
                    } else {
                      toast.success("Profile opened in new tab", { id: toastId });
                    }
                    // Give the browser time to load the blob before revoking.
                    setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
                  } catch (e) {
                    toast.error(`Profile failed: ${e.message}`, { id: toastId });
                  }
                }}
                className="text-sm font-bold px-4 py-2 rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90 inline-flex items-center gap-1.5"
                title="Preview + print the Ditch Careers Candidate Profile"
              >
                <FileText size={14}/>Profile
              </button>
              <button
                data-testid="candidate-detail-branded-cv-download"
                onClick={async () => {
                  try {
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/candidates/${candidateId}/branded-cv?format=download`;
                    const r = await fetch(url, { headers: { "X-Admin-Password": pw } });
                    if (!r.ok) {
                      let msg = `HTTP ${r.status}`;
                      try { const j = await r.json(); if (j?.detail) msg = j.detail; } catch (_) {}
                      throw new Error(msg);
                    }
                    const blob = await r.blob();
                    const objUrl = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = objUrl;
                    const cd = r.headers.get("content-disposition") || "";
                    const m = cd.match(/filename="([^"]+)"/);
                    a.download = m ? m[1] : `ditch-careers-candidate-profile-${candidateId.slice(0,8)}.html`;
                    document.body.appendChild(a); a.click(); a.remove();
                    setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
                    toast.success("HTML profile downloaded");
                  } catch (e) { toast.error(`Download failed: ${e.message}`); }
                }}
                className="text-xs font-bold px-3 py-2 rounded-full bg-white text-[#0D2F4D] border border-[#D6DCE3] hover:border-[#0D2F4D] inline-flex items-center gap-1"
                title="Download the Candidate Profile .html file for offline editing"
              >
                <Download size={12}/>
              </button>
              <button
                data-testid="candidate-detail-company-cv"
                onClick={async () => {
                  const toastId = "company-cv";
                  try {
                    // Feb 2026 CV unification: this button now hits the
                    // unified branded-cv generator so admins get a single
                    // consistent layout across every download path.
                    toast.loading("Generating Branded CV…", { id: toastId });
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/candidates/${candidateId}/branded-cv.pdf`;
                    const r = await fetch(url, { headers: { "X-Admin-Password": pw } });
                    if (!r.ok) {
                      let msg = `HTTP ${r.status}`;
                      try { const j = await r.json(); if (j?.detail) msg = j.detail; } catch (_) {}
                      throw new Error(msg);
                    }
                    const blob = await r.blob();
                    const objUrl = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = objUrl;
                    const cd = r.headers.get("content-disposition") || "";
                    const m = cd.match(/filename="([^"]+)"/);
                    a.download = m ? m[1] : `ditch-careers-cv-${candidateId.slice(0,8)}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
                    toast.success("Branded CV downloaded", { id: toastId });
                  } catch (e) { toast.error(`Branded CV failed: ${e.message}`, { id: toastId }); }
                }}
                className="text-sm font-bold px-4 py-2 rounded-full bg-white text-[#0D2F4D] border-2 border-[#C6D21F] hover:bg-[#F2F2EF] inline-flex items-center gap-1.5"
                title="Downloads the branded Candidate Profile PDF — unified generator across all buttons"
              >
                <Download size={14}/>Branded CV
              </button>
              <button
                data-testid="candidate-detail-polish-profile"
                onClick={onRequestEdit}
                className="text-sm font-bold px-4 py-2 rounded-full bg-[#C6D21F]/20 text-[#0D2F4D] border border-[#C6D21F] hover:bg-[#C6D21F]/30 inline-flex items-center gap-1.5"
                title="Polish the candidate's headline, summary, and experience before generating the PDF"
              >
                Polish profile
              </button>
              <button
                data-testid="candidate-detail-edit"
                onClick={onRequestEdit}
                className="text-sm font-bold px-4 py-2 rounded-full bg-white text-[#0D2F4D] border border-[#D6DCE3] hover:border-[#0D2F4D]"
              >
                Edit details
              </button>
            </div>
          </div>

          {/* Header stat row */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 divide-x divide-[#F2F2EF]">
            <MiniStat label="Type" value={c.candidate_type === "relocating" ? "Relocating" : "Domestic AU"} />
            <MiniStat label="Industry" value={c.industry || "—"} />
            <MiniStat label="Experience" value={c.years_experience != null ? `${c.years_experience} yrs` : "—"} />
            <MiniStat label="Availability" value={c.availability || "—"} />
          </div>

          {/* SEEK registration status badge */}
          {c.seek_registration_status === "sent" && (
            <div
              className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C6D21F]/20 border border-[#0D2F4D]/20 text-[#0D2F4D] text-xs font-bold"
              data-testid="seek-registration-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#0D2F4D]"/>
              Registration email sent
              {c.seek_registration_sent_at && (
                <span className="text-[#6F8FA8] font-normal">· {relTime(c.seek_registration_sent_at)}</span>
              )}
            </div>
          )}
          {c.seek_registration_status === "failed" && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs font-bold" data-testid="seek-registration-badge-failed">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500"/>
              Registration send failed — retry from Recruiter tab
            </div>
          )}
        </div>

        {/* Stage kanban selector */}
        <div className="p-4 bg-[#F2F2EF]" data-testid="candidate-stage-bar">
          <p className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold mb-2">Application stage</p>
          <div className="flex flex-wrap gap-1.5">
            {CANDIDATE_STAGES.map((s) => (
              <button
                key={s.v}
                data-testid={`candidate-stage-${s.v}`}
                onClick={() => setStage(s.v)}
                className={cx(
                  "px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest border transition-all",
                  c.stage === s.v
                    ? "text-white shadow-inner"
                    : "text-[#6F8FA8] border-[#D6DCE3] hover:text-[#0D2F4D] hover:border-[#0D2F4D] bg-white"
                )}
                style={c.stage === s.v ? { background: s.color, borderColor: s.color } : {}}
              >
                {s.label}
              </button>
            ))}
          </div>
          {c.stage_updated_at && (
            <p className="text-[10px] text-[#6F8FA8] mt-2">Updated {relTime(c.stage_updated_at)}</p>
          )}
        </div>

        {/* Priya's post-call lifecycle status — separate from stage */}
        <LifecycleStatusBar
          candidateId={candidateId}
          value={c.lifecycle_status || ""}
          updatedAt={c.lifecycle_status_updated_at}
          pw={pw}
          onSaved={load}
        />

        {/* Tabs */}
        <div className="border-b border-[#F2F2EF] flex flex-wrap px-2">
          {(c.coaching ? [...TABS, { k: "coaching", label: "Coaching", Icon: GraduationCap }] : TABS).map(({ k, label, Icon }) => (
            <button
              key={k}
              data-testid={`candidate-tab-${k}`}
              onClick={() => setTab(k)}
              className={cx(
                "px-4 py-3 text-xs font-bold inline-flex items-center gap-1.5 border-b-2 transition-colors",
                tab === k
                  ? "border-[#C6D21F] text-[#0D2F4D]"
                  : "border-transparent text-[#6F8FA8] hover:text-[#0D2F4D]"
              )}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
      </div>
      <CandidateEmailDialog open={sendOpen} onClose={() => setSendOpen(false)} candidateId={candidateId} candidate={c} contact={p} pw={pw} currentUser={currentUser} onSent={() => { setSendOpen(false); loadComms(); loadTimeline(); }}/>

      {/* Tab content */}
      <div className="bg-white rounded-2xl border border-[#E4E7EA] p-6" data-testid={`candidate-tabpanel-${tab}`}>
        {tab === "overview" && (
          <>
            <OverviewPanel c={c} p={p} />
            <div className="mt-4">
              <TasksSection pw={pw} entityType="candidate" entityId={candidateId} />
            </div>
          </>
        )}
        {tab === "parsed_cv" && <ParsedCvPanel candidate={c} />}
        {tab === "full_history" && <FullHistoryPanel candidate={c} />}
        {tab === "recruiter" && <RecruiterPanel candidateId={candidateId} pw={pw} record={record} onChange={load} />}
        {tab === "branded"   && <BrandedProfilePanel candidateId={candidateId} pw={pw} record={record} onChange={load} />}
        {tab === "conversations" && <ConversationsPanel entityType="candidate" entityId={candidateId} pw={pw} />}
        {tab === "timeline" && <TimelinePanel items={timeline} pw={pw} onChange={loadTimeline} />}
        {tab === "notes"    && <NotesPanel entityId={candidateId} pw={pw} items={notes} onChange={loadNotes} />}
        {tab === "comms"    && <CommsPanel entityId={candidateId} entityType="candidate" pw={pw} items={comms} onChange={loadComms} />}
        {tab === "documents" && (
          <DocumentsPanel
            docs={mergeCvIntoDocs(c)}
            entityId={candidateId}
            entityType="candidate"
            pw={pw}
            onChange={load}
          />
        )}
        {tab === "applications" && <ApplicationsPanel items={applications} pw={pw} candidateId={candidateId} onChange={loadApps} />}
        {tab === "submitted" && (
          <CandidateSubmissions
            candidateId={candidateId}
            candidateName={[c.person?.first_name || c.first_name, c.person?.last_name || c.last_name].filter(Boolean).join(" ") || "Candidate"}
            pw={pw}
          />
        )}
        {tab === "shortlist_activity" && (
          <CandidateShortlistActivityPanel activity={c.shortlist?.activity || {}} candidateId={candidateId} />
        )}
        {tab === "coaching" && (
          <CoachingPanel candidateId={candidateId} pw={pw} coaching={c.coaching || {}} onChange={load} />
        )}
      </div>
    </div>
  );
}

/* ─── Employment Preparation & Coaching Package panel ─────────────────── */
function CoachingPanel({ candidateId, pw, coaching, onChange }) {
  const H = useMemo(() => authHeader(pw), [pw]);
  const [form, setForm] = useState({
    status: coaching.status || "Payment Received",
    support_start: (coaching.support_start || "").slice(0, 10),
    support_end: (coaching.support_end || "").slice(0, 10),
    current_cv_received: !!coaching.current_cv_received,
    linkedin_received: !!coaching.linkedin_received,
    strategy_session_date: (coaching.strategy_session_date || "").slice(0, 10),
    interview_coaching_date: (coaching.interview_coaching_date || "").slice(0, 10),
    weekly_checkin_notes: coaching.weekly_checkin_notes || "",
    package_notes: coaching.package_notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [registering, setRegistering] = useState(false);
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const next = {
        ...coaching,
        status: form.status,
        support_start: form.support_start || null,
        support_end: form.support_end || null,
        current_cv_received: form.current_cv_received,
        linkedin_received: form.linkedin_received,
        strategy_session_date: form.strategy_session_date || null,
        interview_coaching_date: form.interview_coaching_date || null,
        weekly_checkin_notes: form.weekly_checkin_notes,
        package_notes: form.package_notes,
      };
      await axios.patch(`${BASE}/candidates/${candidateId}`, { coaching: next }, H);
      toast.success("Coaching record saved.");
      onChange?.();
    } catch {
      toast.error("Couldn't save the coaching record.");
    } finally {
      setSaving(false);
    }
  };

  const createCareers = async () => {
    setRegistering(true);
    try {
      await axios.post(`${BASE}/candidates/${candidateId}/create-careers-registration`, {}, H);
      toast.success("Ditch Careers registration created.");
      onChange?.();
    } catch {
      toast.error("Couldn't create the Careers registration.");
    } finally {
      setRegistering(false);
    }
  };

  const fld = "w-full rounded-lg border border-[#E4E7EA] px-3 py-2 text-sm focus:border-[#0D2F4D] outline-none";
  const lbl = "block text-[10px] uppercase tracking-widest font-bold text-[#0D2F4D] mb-1";
  const consent = coaching.consent || {};

  return (
    <div className="space-y-6" data-testid="coaching-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#0D2F4D] bg-[#C6D21F] px-3 py-1 rounded-full">
            <GraduationCap size={12} /> Employment Coaching Package
          </span>
          {coaching.purchase_date && (
            <p className="text-xs text-[#6F8FA8] mt-2">Purchased {new Date(coaching.purchase_date).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })}</p>
          )}
        </div>
        <button
          onClick={createCareers}
          disabled={registering || coaching.careers_registration_created}
          data-testid="coaching-create-careers"
          className="text-xs font-bold px-4 py-2 rounded-full bg-[#0D2F4D] text-white hover:bg-[#1a4063] disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {coaching.careers_registration_created ? <><CheckCircle2 size={13} /> Careers registration created</> : <>Create Careers Registration</>}
        </button>
      </div>

      <div>
        <span className={lbl}>Progress stage</span>
        <div className="flex flex-wrap gap-2" data-testid="coaching-stages">
          {COACHING_STAGES.map((s) => (
            <button
              key={s}
              onClick={() => upd({ status: s })}
              data-testid={`coaching-stage-${s.replace(/\s+/g, "-").toLowerCase()}`}
              className={cx(
                "text-xs font-bold px-3 py-1.5 rounded-full border transition-colors",
                form.status === s ? "bg-[#0D2F4D] text-white border-[#0D2F4D]" : "bg-white text-[#0D2F4D] border-[#E4E7EA] hover:border-[#0D2F4D]"
              )}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block"><span className={lbl}>Support start date</span>
          <input type="date" value={form.support_start} onChange={(e) => upd({ support_start: e.target.value })} className={fld} data-testid="coaching-support-start" /></label>
        <label className="block"><span className={lbl}>Support end date</span>
          <input type="date" value={form.support_end} onChange={(e) => upd({ support_end: e.target.value })} className={fld} data-testid="coaching-support-end" /></label>
        <label className="block"><span className={lbl}>Strategy session date</span>
          <input type="date" value={form.strategy_session_date} onChange={(e) => upd({ strategy_session_date: e.target.value })} className={fld} data-testid="coaching-strategy-date" /></label>
        <label className="block"><span className={lbl}>Interview coaching date</span>
          <input type="date" value={form.interview_coaching_date} onChange={(e) => upd({ interview_coaching_date: e.target.value })} className={fld} data-testid="coaching-interview-date" /></label>
      </div>

      <div className="flex flex-wrap gap-6">
        <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[#0D2F4D]">
          <input type="checkbox" checked={form.current_cv_received} onChange={(e) => upd({ current_cv_received: e.target.checked })} className="w-4 h-4 rounded" data-testid="coaching-cv-received" />
          Current CV received
        </label>
        <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-[#0D2F4D]">
          <input type="checkbox" checked={form.linkedin_received} onChange={(e) => upd({ linkedin_received: e.target.checked })} className="w-4 h-4 rounded" data-testid="coaching-linkedin-received" />
          LinkedIn profile received
        </label>
      </div>

      <label className="block"><span className={lbl}>Weekly check-in notes</span>
        <textarea rows={4} value={form.weekly_checkin_notes} onChange={(e) => upd({ weekly_checkin_notes: e.target.value })} className={fld} data-testid="coaching-checkin-notes" placeholder="Log each weekly check-in…" /></label>
      <label className="block"><span className={lbl}>Package notes</span>
        <textarea rows={3} value={form.package_notes} onChange={(e) => upd({ package_notes: e.target.value })} className={fld} data-testid="coaching-package-notes" placeholder="Anything else about this package…" /></label>

      {consent.accepted_at && (
        <div className="rounded-lg bg-[#F7F7F4] border border-[#E4E7EA] p-4 text-xs text-[#6F8FA8] leading-relaxed" data-testid="coaching-consent-record">
          <b className="text-[#0D2F4D]">Consent recorded</b> — {consent.name || "—"} ({consent.email || "—"}) accepted the coaching terms on{" "}
          {new Date(consent.accepted_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })} · version {consent.package_version || "—"} · payment {consent.payment_status || "—"}.
        </div>
      )}

      <div>
        <button onClick={save} disabled={saving} data-testid="coaching-save"
          className="text-sm font-bold px-5 py-2.5 rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:brightness-105 disabled:opacity-50 inline-flex items-center gap-1.5">
          <Save size={14} /> {saving ? "Saving…" : "Save coaching record"}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────── panels ─────────────────────────────── */

// Show the Careers-ingested CV inside the Documents tab even when it wasn't
// added via the internal upload flow. When the Careers app posted only a
// bare `cv_url` + `cv_filename` (no `documents` array), synthesise a
// display-only entry so recruiters can click through to the CV.
function mergeCvIntoDocs(c) {
  const docs = Array.isArray(c?.documents) ? [...c.documents] : [];
  const cvUrl = (c?.cv_url || "").trim();
  if (!cvUrl) return docs;
  const already = docs.some((d) => (d?.url || "").trim() === cvUrl);
  if (already) return docs;
  docs.unshift({
    id: `ingested-cv-${c?.id || "x"}`,
    name: c?.cv_filename || "CV (from Careers site)",
    url: cvUrl,
    kind: "cv",
    source: "talent_hub_ingest",
    ingested: true,
  });
  return docs;
}

/* ── Conversations — unified Gmail timeline (sent + received) ─────────── */
function ReplyBox({ msg, entityType, entityId, contactEmail, accounts, pw, onSent }) {
  const [open, setOpen] = useState(false);
  const [fromAcct, setFromAcct] = useState(accounts[0]?.admin_email || "");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const to = contactEmail || msg.contact_person || "";

  useEffect(() => { if (!fromAcct && accounts[0]) setFromAcct(accounts[0].admin_email); }, [accounts, fromAcct]);

  const send = async () => {
    if (!text.trim()) { toast.error("Write a reply first."); return; }
    if (!to) { toast.error("No recipient email on this conversation."); return; }
    setSending(true);
    try {
      const subject = /^re:/i.test(msg.subject) ? msg.subject : `Re: ${msg.subject}`;
      await axios.post(`${GMAIL_BASE}/send`, {
        to_email: to,
        subject,
        body: text.trim(),
        thread_id: msg.thread_id || undefined,
        from_admin_email: fromAcct || undefined,
        entity_type: entityType,
        entity_id: entityId,
      }, authHeader(pw));
      toast.success(`Reply sent to ${to}.`);
      setText(""); setOpen(false);
      onSent?.();
    } catch (e) {
      toast.error(`Send failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSending(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid={`conversation-reply-open-${msg.message_id}`}
        className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90"
      >
        <Mail size={13}/>Reply
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-[#D6DCE3] bg-white p-3 space-y-2" data-testid={`conversation-reply-box-${msg.message_id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] whitespace-nowrap">Reply to</span>
        <span className="text-sm text-[#0D2F4D] font-medium flex-1 truncate">{to}</span>
        <span className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] whitespace-nowrap">From</span>
        {accounts.length > 0 ? (
          <select
            value={fromAcct}
            onChange={(e) => setFromAcct(e.target.value)}
            data-testid={`conversation-reply-from-${msg.message_id}`}
            className="text-sm border border-[#D6DCE3] rounded-lg px-2 py-1.5 bg-white text-[#0D2F4D] max-w-[240px]"
          >
            {accounts.map(a => <option key={a.admin_email} value={a.admin_email}>{a.gmail_email}</option>)}
          </select>
        ) : (
          <span className="text-xs text-red-600">No Gmail connected — link one in Integrations.</span>
        )}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder={`Write your reply to ${to}…`}
        data-testid={`conversation-reply-text-${msg.message_id}`}
        className="w-full text-sm border border-[#D6DCE3] rounded-lg p-3 focus:outline-none focus:border-[#0D2F4D]"
      />
      <div className="flex justify-end gap-2">
        <button onClick={() => { setOpen(false); setText(""); }} className="text-sm font-medium px-4 py-2 rounded-full text-[#6F8FA8] hover:text-[#0D2F4D]">Cancel</button>
        <button
          onClick={send}
          disabled={sending || accounts.length === 0}
          data-testid={`conversation-reply-send-${msg.message_id}`}
          className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#0D2F4D]/90 disabled:opacity-50"
        >
          <ArrowUpRight size={13}/>{sending ? "Sending…" : "Send reply"}
        </button>
      </div>
    </div>
  );
}

function MessageRow({ entityType, entityId, contactEmail, accounts, pw, msg, onSent }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const inbound = msg.direction === "in";

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail && msg.message_id) {
      setLoading(true);
      try {
        const r = await axios.get(`${GMAIL_BASE}/message/${msg.message_id}`, authHeader(pw));
        setDetail(r.data);
      } catch (e) {
        toast.error(`Couldn't load message: ${e?.response?.data?.detail || e.message}`);
      } finally { setLoading(false); }
    }
  };

  const dl = (att) => {
    const url = `${GMAIL_BASE}/message/${msg.message_id}/attachment/${att.attachment_id}`
      + `?filename=${encodeURIComponent(att.filename)}&x_admin_password=${encodeURIComponent(pw)}`;
    window.open(url, "_blank");
  };

  return (
    <div className="border border-[#E7E2D8] rounded-xl overflow-hidden bg-white" data-testid={`conversation-row-${msg.message_id}`}>
      <button
        onClick={toggle}
        data-testid={`conversation-toggle-${msg.message_id}`}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#F7F5F0] transition-colors"
      >
        <span className={cx(
          "flex-shrink-0 w-8 h-8 rounded-full grid place-items-center",
          inbound ? "bg-[#C6D21F]/25 text-[#0D2F4D]" : "bg-[#0D2F4D] text-[#C6D21F]"
        )}>
          {inbound ? <ArrowDownLeft size={15}/> : <ArrowUpRight size={15}/>}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={cx("text-[10px] uppercase tracking-widest font-bold",
              inbound ? "text-[#6B8E23]" : "text-[#6F8FA8]")}>
              {inbound ? "Received" : "Sent"}
            </span>
            <span className="text-xs text-[#9AA7B2]">{relTime(msg.at)}</span>
          </div>
          <div className="font-semibold text-[#0D2F4D] truncate">{msg.subject}</div>
          {!open && <div className="text-xs text-[#6F8FA8] truncate">{msg.snippet}</div>}
        </div>
        <span className="flex items-center gap-1 text-xs font-bold text-[#0D2F4D] flex-shrink-0">
          {open ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
          <span className="hidden sm:inline">View message</span>
        </span>
      </button>

      {open && (
        <div className="border-t border-[#E7E2D8] px-4 py-4 bg-[#FCFBF8]" data-testid={`conversation-body-${msg.message_id}`}>
          {loading && <p className="text-sm text-[#6F8FA8] py-4">Loading full message…</p>}
          {!loading && detail && (
            <>
              <div className="text-xs text-[#6F8FA8] space-y-0.5 mb-3">
                <div><span className="font-semibold text-[#0D2F4D]">From:</span> {detail.from}</div>
                <div><span className="font-semibold text-[#0D2F4D]">To:</span> {detail.to}</div>
                {detail.date && <div><span className="font-semibold text-[#0D2F4D]">Date:</span> {detail.date}</div>}
              </div>
              {detail.body_html
                ? <div className="prose prose-sm max-w-none text-[#1F2D3A] bg-white rounded-lg p-4 border border-[#EEE9DE] overflow-x-auto"
                       dangerouslySetInnerHTML={{ __html: detail.body_html }} />
                : <pre className="whitespace-pre-wrap font-sans text-sm text-[#1F2D3A] bg-white rounded-lg p-4 border border-[#EEE9DE]">{detail.body_text}</pre>}
              {(detail.attachments || []).length > 0 && (
                <div className="mt-4">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] mb-2">Attachments</p>
                  <div className="flex flex-wrap gap-2">
                    {detail.attachments.map((att) => (
                      <button
                        key={att.attachment_id}
                        onClick={() => dl(att)}
                        data-testid={`attachment-${att.attachment_id}`}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#0D2F4D]/20 hover:border-[#0D2F4D] text-sm text-[#0D2F4D] font-medium transition-colors"
                      >
                        <Paperclip size={13}/>
                        <span className="truncate max-w-[200px]">{att.filename}</span>
                        <Download size={13} className="text-[#6B8E23]"/>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          {!loading && !detail && <p className="text-sm text-[#6F8FA8] py-4">{msg.snippet || "No preview available."}</p>}
          <ReplyBox
            msg={msg} entityType={entityType} entityId={entityId}
            contactEmail={contactEmail} accounts={accounts} pw={pw} onSent={onSent}
          />
        </div>
      )}
    </div>
  );
}

export function ConversationsPanel({ entityType = "candidate", entityId, pw }) {
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [contactEmail, setContactEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const qparam = entityType === "employer" ? `employer_id=${entityId}` : `candidate_id=${entityId}`;
  const noun = entityType === "employer" ? "employer" : "candidate";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${GMAIL_BASE}/conversations?${qparam}`, authHeader(pw));
      setRows(r.data.conversations || []);
      setContactEmail(r.data.contact_email || "");
    } catch (e) {
      toast.error(`Conversations failed to load: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [qparam, pw]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${GMAIL_BASE}/accounts`, authHeader(pw))
      .then(r => setAccounts(r.data.accounts || []))
      .catch(() => {});
  }, [pw]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      await axios.post(`${GMAIL_BASE}/sync-now`, { days: 7 }, authHeader(pw));
      toast.success("Inbox synced — refreshing conversations.");
      await load();
    } catch (e) {
      toast.error(`Sync failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSyncing(false); }
  };

  return (
    <div className="space-y-4" data-testid="conversations-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-[#0D2F4D]">Conversations</h3>
          <p className="text-sm text-[#6F8FA8]">Every email sent to and received from this {noun}, synced from Gmail.</p>
        </div>
        <button
          onClick={syncNow}
          disabled={syncing}
          data-testid="conversations-sync"
          className="inline-flex items-center gap-1.5 text-sm font-bold px-4 py-2 rounded-full bg-white text-[#0D2F4D] border border-[#D6DCE3] hover:border-[#0D2F4D] disabled:opacity-50"
        >
          <RefreshCw size={13} className={syncing ? "animate-spin" : ""}/>{syncing ? "Syncing…" : "Sync now"}
        </button>
      </div>

      {loading ? (
        <p className="text-center text-[#6F8FA8] py-12">Loading conversations…</p>
      ) : rows.length === 0 ? (
        <EmptyState text={`No emails yet — once you email this ${noun} or they reply, the thread appears here. Hit 'Sync now' to pull the latest from Gmail.`} />
      ) : (
        <div className="space-y-2" data-testid="conversations-list">
          {rows.map((m, i) => (
            <MessageRow
              key={m.message_id || i}
              entityType={entityType} entityId={entityId}
              contactEmail={contactEmail} accounts={accounts}
              pw={pw} msg={m} onSent={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function OverviewPanel({ c, p }) {
  const rows = [
    ["Email", p?.email],
    ["Phone", p?.phone],
    ["Location now", c.location_now],
    ["Location target", c.location_target],
    ["Current company", c.current_company],
    ["Industry", c.industry],
    ["Role level", c.role_level],
    ["Years experience", c.years_experience],
    ["Availability", c.availability],
    ["Salary expectation", c.salary_expectation],
    ["LinkedIn", c.linkedin],
    ["CV URL", c.cv_url],
  ];
  const visaRows = c.candidate_type === "relocating"
    ? [["Visa status", c.visa_status], ["Visa type", c.visa_type]]
    : [];
  // ── Ingested-profile shortcuts (from external Careers app) ──────────
  const workHistory = Array.isArray(c.work_history) ? c.work_history : [];
  const qualifications = Array.isArray(c.qualifications) ? c.qualifications : [];
  const softwareSkills = Array.isArray(c.software_skills)
    ? c.software_skills.filter(Boolean) : [];
  const languages = Array.isArray(c.languages) ? c.languages : [];
  const nextRole = (c.next_role && typeof c.next_role === "object") ? c.next_role : null;
  const workRights = (c.work_rights && typeof c.work_rights === "object") ? c.work_rights : null;
  const employment = (c.employment && typeof c.employment === "object") ? c.employment : null;

  return (
    <div className="space-y-6">
      {/* Headline / Summary hero — from Careers app ingest */}
      {(!empty(c.headline) || !empty(c.summary) || !empty(c.candidate_status)) && (
        <div className="bg-[#FAFAF7] rounded-xl p-4 border-l-4 border-[#C6D21F]" data-testid="candidate-hero-card">
          {!empty(c.headline) && (
            <p className="text-lg font-bold text-[#0D2F4D] leading-snug" data-testid="candidate-headline">
              {c.headline}
            </p>
          )}
          {!empty(c.candidate_status) && (
            <span
              data-testid="candidate-status"
              className="inline-block mt-1 text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8]"
            >
              {String(c.candidate_status).replaceAll("_", " ")}
            </span>
          )}
          {!empty(c.summary) && (
            <p
              data-testid="candidate-summary"
              className="text-sm text-[#0D2F4D] whitespace-pre-line mt-3 leading-relaxed"
            >
              {c.summary}
            </p>
          )}
        </div>
      )}

      <div>
        <SectionHeader title="Details" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm min-w-0">
          {[...rows, ...visaRows].map(([label, v]) => (
            <div key={label} className="flex gap-2 min-w-0">
              <span className="shrink-0 text-[#6F8FA8] text-[11px] uppercase tracking-widest font-bold whitespace-nowrap w-32 md:w-40">
                {label}
              </span>
              {(label === "LinkedIn" || label === "CV URL") && !empty(v) ? (
                <a href={v} target="_blank" rel="noreferrer" className="text-[#0D2F4D] underline break-all">{v}</a>
              ) : (
                <span className="min-w-0 break-words text-[#0D2F4D]">{empty(v) ? "—" : String(v)}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Situation — free-form self-description from intake form */}
      {!empty(c.situation) && (
        <div>
          <SectionHeader title="Situation" />
          <p
            data-testid="candidate-situation"
            className="text-sm text-[#0D2F4D] whitespace-pre-line bg-[#FAFAF7] p-4 rounded-xl leading-relaxed"
          >
            {c.situation}
          </p>
        </div>
      )}

      {/* Work history — array of {title, company, start, end, description} */}
      {workHistory.length > 0 && (
        <div data-testid="candidate-work-history">
          <SectionHeader title="Work history" />
          <ol className="relative border-l-2 border-[#F2F2EF] ml-3 space-y-4">
            {workHistory.map((w, i) => {
              const title = w.title || w.role || w.position || "";
              const company = w.company || w.employer || "";
              const range = [w.start, w.end].filter(Boolean).join(" – ") || w.dates || "";
              const desc = w.description || w.summary || w.raw || "";
              return (
                <li key={`${title}-${company}-${range}-${i}`} className="ml-5 relative">
                  <span className="absolute -left-[27px] top-1.5 w-3 h-3 rounded-full border-2 border-white shadow bg-[#C6D21F]" />
                  {(title || company) && (
                    <p className="text-sm font-bold text-[#0D2F4D]">
                      {title}{title && company ? " · " : ""}{company}
                    </p>
                  )}
                  {range && (
                    <p className="text-[11px] uppercase tracking-widest text-[#6F8FA8] mt-0.5">
                      {range}
                    </p>
                  )}
                  {desc && (
                    <p className="text-sm text-[#0D2F4D] mt-1 whitespace-pre-line">{desc}</p>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Qualifications — array of {name/qualification, institution, year} */}
      {qualifications.length > 0 && (
        <div data-testid="candidate-qualifications">
          <SectionHeader title="Qualifications" />
          <ul className="space-y-2">
            {qualifications.map((q, i) => {
              const name = q.name || q.qualification || q.degree || q.title || q.raw || "";
              const institution = q.institution || q.school || q.provider || "";
              const year = q.year || q.completed || q.date || "";
              return (
                <li
                  key={`${name}-${institution}-${year}-${i}`}
                  className="bg-[#FAFAF7] rounded-xl p-3 text-sm text-[#0D2F4D]"
                >
                  <p className="font-semibold">{name || "—"}</p>
                  {(institution || year) && (
                    <p className="text-xs text-[#6F8FA8] mt-0.5">
                      {institution}{institution && year ? " · " : ""}{year}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Software skills — coerced string array from ingest */}
      {softwareSkills.length > 0 && (
        <div data-testid="candidate-software-skills">
          <SectionHeader title="Software skills" />
          <div className="flex flex-wrap gap-1.5">
            {softwareSkills.map((s, i) => (
              <span
                key={`${s}-${i}`}
                className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-[#0D2F4D] text-white"
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {(c.skills || []).length > 0 && (
        <div>
          <SectionHeader title="Other skills" />
          <div className="flex flex-wrap gap-1.5">
            {c.skills.map((s) => (
              <span key={s} className="text-xs font-semibold px-2 py-1 rounded-lg bg-[#F2F2EF] text-[#0D2F4D]">
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Languages — array of {name, level} */}
      {languages.length > 0 && (
        <div data-testid="candidate-languages">
          <SectionHeader title="Languages" />
          <div className="flex flex-wrap gap-1.5">
            {languages.map((l, i) => {
              const name = l.name || l.language || l.label || "";
              const level = l.level || l.proficiency || "";
              if (!name) return null;
              return (
                <span
                  key={`${name}-${i}`}
                  className="text-xs px-2.5 py-1 rounded-lg bg-[#F2F2EF] text-[#0D2F4D]"
                >
                  <span className="font-semibold">{name}</span>
                  {level && <span className="ml-1 text-[#6F8FA8]">· {level}</span>}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Compact chips for the four dict fields — hidden when empty */}
      {(nextRole || workRights || employment || (c.profile_all && Object.keys(c.profile_all).length > 0)) && (
        <div data-testid="candidate-discovery-block">
          <SectionHeader title="Profile & onboarding" />
          <p className="text-[11px] text-[#94A3B8] mb-3">
            Everything the candidate answered on the Careers questionnaire. Blanks are hidden.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {nextRole && (
              <div data-testid="candidate-next-role" className="bg-[#FAFAF7] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] mb-1.5">
                  Next role
                </p>
                <DictSummary d={nextRole} />
              </div>
            )}
            {workRights && (
              <div data-testid="candidate-work-rights" className="bg-[#FAFAF7] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] mb-1.5">
                  Work rights
                </p>
                <DictSummary d={workRights} />
              </div>
            )}
            {employment && (
              <div data-testid="candidate-employment" className="bg-[#FAFAF7] rounded-xl p-3">
                <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] mb-1.5">
                  Employment
                </p>
                <DictSummary d={employment} />
              </div>
            )}
          </div>
          {c.profile_all && typeof c.profile_all === "object" && (() => {
            // Sep 2026: also render every OTHER answer from the questionnaire
            // catch-all (`profile_all`) that isn't already shown in the three
            // dict tiles above. Recruiters kept saying "the discovery info
            // isn't pulling through" — this is the fix.
            const shownKeys = new Set([
              ...Object.keys(nextRole || {}),
              ...Object.keys(workRights || {}),
              ...Object.keys(employment || {}),
              // Outer keys already shown as their own tiles above.
              "next_role", "work_rights", "employment",
              // Internal identifiers / meta / timestamps — never useful here.
              "id", "person_id", "user_id", "candidate_id", "account_id", "user",
              "candidate_status", "careers_access", "access_checked_at",
              "onboarding_step", "submitted", "created_at", "updated_at",
              "source", "brand", "_id",
              // Also hide keys we already surface elsewhere on the profile.
              "email","first_name","last_name","phone","linkedin","linkedin_url",
              "headline","summary","situation","work_history","qualifications",
              "software_skills","languages","cv_url","current_location",
              "location_now","location_target","industry","role_title",
              "role_level","availability",
            ]);
            const extra = Object.fromEntries(
              Object.entries(c.profile_all || {}).filter(
                ([k, v]) => !shownKeys.has(k) && v !== null && v !== undefined && v !== "" &&
                            !(Array.isArray(v) && v.length === 0),
              ),
            );
            if (Object.keys(extra).length === 0) return null;
            return (
              <div data-testid="candidate-profile-extras" className="bg-[#FAFAF7] rounded-xl p-4 mt-4">
                <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] mb-2">
                  Discovery answers
                </p>
                <DictSummary d={extra} />
              </div>
            );
          })()}
        </div>
      )}

      {c.notes && (
        <div>
          <SectionHeader title="Notes" />
          <p className="text-sm text-[#0D2F4D] whitespace-pre-line bg-[#FAFAF7] p-4 rounded-xl">{c.notes}</p>
        </div>
      )}
    </div>
  );
}

// Small helper — renders every non-empty key/value from a dict as a clean,
// human-readable mini-list. Nested objects (e.g. qualifications_details) are
// rendered as an indented sub-list instead of raw JSON; booleans become
// Yes/No; arrays become comma lists. Internal/meta keys are hidden.
const DICT_HUMAN = (k) =>
  String(k)
    .replaceAll("_", " ")
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
const DICT_NOISE_KEYS = new Set([
  "id", "person_id", "user_id", "candidate_id", "account_id", "user",
  "candidate_status", "careers_access", "access_checked_at", "onboarding_step",
  "submitted", "created_at", "updated_at", "source", "brand", "_id",
]);
function fmtDictVal(v) {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}
function dictEntries(d) {
  return Object.entries(d || {}).filter(([k, v]) =>
    !DICT_NOISE_KEYS.has(k) &&
    v !== null && v !== undefined && v !== "" &&
    !(Array.isArray(v) && v.length === 0) &&
    !(typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0),
  );
}
function DictSummary({ d }) {
  const entries = dictEntries(d);
  if (!entries.length) {
    return <p className="text-xs text-[#6F8FA8]">—</p>;
  }
  return (
    <dl className="text-xs text-[#0D2F4D] space-y-1.5">
      {entries.map(([k, v]) => {
        const isObj = v && typeof v === "object" && !Array.isArray(v);
        if (isObj) {
          const sub = dictEntries(v);
          if (!sub.length) return null;
          return (
            <div key={k}>
              <dt className="text-[#6F8FA8]">{DICT_HUMAN(k)}</dt>
              <dd className="mt-1 ml-1 pl-3 border-l border-[#E5E7EB]">
                <dl className="space-y-1">
                  {sub.map(([sk, sv]) => (
                    <div key={sk} className="flex gap-2">
                      <dt className="text-[#6F8FA8] whitespace-nowrap">{DICT_HUMAN(sk)}</dt>
                      <dd className="font-medium break-words">{fmtDictVal(sv)}</dd>
                    </div>
                  ))}
                </dl>
              </dd>
            </div>
          );
        }
        return (
          <div key={k} className="flex gap-2">
            <dt className="text-[#6F8FA8] whitespace-nowrap">{DICT_HUMAN(k)}</dt>
            <dd className="font-medium break-words">{fmtDictVal(v)}</dd>
          </div>
        );
      })}
    </dl>
  );
}

const BOOKING_STATUS_COLORS = {
  active: "#22C55E", cancelled: "#94A3B8", rescheduled: "#38BDF8", no_show: "#EF4444",
};

export function TimelinePanel({ items, pw, onChange }) {
  const [marking, setMarking] = useState("");
  const markNoShow = async (bookingId) => {
    setMarking(bookingId);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/automations/bookings/${bookingId}/no-show`,
        {}, authHeader(pw),
      );
      const n = (r.data.enrolled || []).length;
      toast.success(n
        ? `Marked as no-show — enrolled into ${n} no-show journey${n === 1 ? "" : "s"}.`
        : "Marked as no-show. No active no-show journey for this brand — activate one in the Email Hub to auto-follow-up.");
      onChange?.();
    } catch (e) {
      toast.error(`Couldn't mark no-show: ${e?.response?.data?.detail || e.message}`);
    } finally { setMarking(""); }
  };

  if (!items.length) return <EmptyState text="No activity yet." />;
  return (
    <ol className="relative border-l-2 border-[#F2F2EF] ml-3 space-y-5">
      {items.map((it) => (
        <li key={`${it.kind}-${it.id}`} className="ml-5 relative">
          <span
            className="absolute -left-[27px] top-1.5 w-3 h-3 rounded-full border-2 border-white shadow"
            style={{ background: it.kind === "note" ? "#C6D21F" : it.kind === "comms" ? "#38BDF8" : it.kind === "booking" ? "#F97316" : "#0D2F4D" }}
          />
          <p className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold">
            {it.kind === "note" ? "Note" : it.kind === "comms" ? `${channelMeta(it.channel).label} · ${it.direction === "in" ? "in" : "out"}` : it.kind === "booking" ? "Calendly booking" : (it.event_type || "event").replaceAll("_", " ")}
            <span className="ml-2 normal-case tracking-normal text-[#94A3B8] font-normal">{relTime(it.at)}</span>
          </p>
          <div className="mt-1 text-sm text-[#0D2F4D]">
            {it.kind === "note" && <p className="whitespace-pre-line">{it.content}</p>}
            {it.kind === "comms" && (
              <>
                {it.subject && <p className="font-semibold">{it.subject}</p>}
                {it.body && <p className="text-[#6F8FA8] mt-0.5 whitespace-pre-line">{it.body}</p>}
              </>
            )}
            {it.kind === "booking" && (
              <div className="flex items-center gap-3 flex-wrap" data-testid={`timeline-booking-${it.id}`}>
                <span className="text-xs text-[#6F8FA8]">
                  {it.scheduled_at ? `Call ${relTime(it.scheduled_at)}` : "Call time unknown"}
                  {it.pipeline ? ` · ${it.pipeline}` : ""}
                </span>
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border"
                  style={{
                    color: BOOKING_STATUS_COLORS[it.status] || "#6F8FA8",
                    borderColor: BOOKING_STATUS_COLORS[it.status] || "#6F8FA8",
                  }}
                  data-testid={`booking-status-${it.id}`}
                >
                  {(it.status || "unknown").replaceAll("_", " ")}
                </span>
                {it.status === "active" && (
                  <button
                    onClick={() => markNoShow(it.id)}
                    disabled={marking === it.id}
                    data-testid={`booking-noshow-${it.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-bold rounded-full border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 transition-colors"
                  >
                    <CalendarX size={11}/>{marking === it.id ? "Marking…" : "Mark as no-show"}
                  </button>
                )}
              </div>
            )}
            {it.kind === "event" && it.payload && Object.keys(it.payload).length > 0 && (
              <p className="text-[#6F8FA8] text-xs">
                {Object.entries(it.payload).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(" · ")}
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

export function NotesPanel({ entityId, pw, items, onChange, entityType = "candidate" }) {
  const [content, setContent] = useState("");
  const [noteType, setNoteType] = useState("note");
  const [happenedAt, setHappenedAt] = useState("");
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      const payload = {
        entity_type: entityType,
        entity_id: entityId,
        content,
        note_type: noteType,
        happened_at: happenedAt || undefined,
      };
      const resp = await axios.post(`${BASE}/notes`, payload, authHeader(pw));
      setContent("");
      setHappenedAt("");
      setNoteType("note");
      // ── AI auto-fill feedback ──────────────────────────────────────────
      // Backend runs the new note through Claude and fills any candidate
      // fields it can extract with high confidence. Show the user exactly
      // what was applied so they know the note wasn't just saved verbatim.
      const applied = resp?.data?.ai_applied || {};
      const appliedKeys = Object.keys(applied);
      if (appliedKeys.length) {
        const label = appliedKeys
          .map((k) => k.replace(/_/g, " "))
          .join(", ");
        toast.success(
          `${noteType === "call" ? "Call logged" : noteType === "meeting" ? "Meeting logged" : "Note added"} — auto-filled: ${label}`,
          { duration: 6000 }
        );
      } else {
        toast.success(noteType === "call" ? "Call logged." : noteType === "meeting" ? "Meeting logged." : "Note added.");
      }
      onChange?.();
    } catch (e) {
      toast.error("Couldn't add note.");
    } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${BASE}/notes/${id}`, authHeader(pw));
      onChange?.();
    } catch { toast.error("Couldn't delete note."); }
  };

  const typePill = (nt) => {
    const m = {
      call:    { label: "Call",    bg: "#38BDF8" },
      meeting: { label: "Meeting", bg: "#FBBF24" },
      note:    { label: "Note",    bg: "#94A3B8" },
    }[nt || "note"];
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-white" style={{ background: m.bg }}>
        {m.label}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#F2F2EF] rounded-xl p-4">
        <div className="flex flex-wrap gap-2 mb-3 items-center">
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8]">Type:</span>
          {[
            { v: "note",    label: "Note" },
            { v: "call",    label: "Phone call" },
            { v: "meeting", label: "Meeting" },
          ].map((t) => (
            <button
              key={t.v}
              onClick={() => setNoteType(t.v)}
              data-testid={`note-type-${t.v}`}
              className={cx(
                "px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest border transition-colors",
                noteType === t.v
                  ? "bg-[#0D2F4D] text-[#C6D21F] border-[#0D2F4D]"
                  : "bg-white text-[#6F8FA8] border-[#D6DCE3] hover:text-[#0D2F4D] hover:border-[#0D2F4D]",
              )}
            >
              {t.label}
            </button>
          ))}
          {(noteType === "call" || noteType === "meeting") && (
            <input
              type="datetime-local"
              value={happenedAt}
              onChange={(e) => setHappenedAt(e.target.value)}
              data-testid="note-happened-at"
              className="ml-auto text-xs border border-[#D6DCE3] rounded-lg px-2 py-1 bg-white text-[#0D2F4D] focus:outline-none focus:border-[#0D2F4D]"
            />
          )}
        </div>
        <textarea
          data-testid="candidate-note-input"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={
            noteType === "call"    ? "What was discussed on the call? Outcome, follow-up needed…"
            : noteType === "meeting" ? "Meeting details: attendees, agenda, decisions…"
            : "Add a quick note about this candidate…"
          }
          rows={3}
          className="w-full rounded-lg border border-[#D6DCE3] bg-white px-3 py-2 text-sm text-[#0D2F4D] focus:outline-none focus:border-[#0D2F4D] resize-y"
        />
        <div className="flex justify-end mt-2">
          <button
            data-testid="candidate-note-add"
            disabled={saving || !content.trim()}
            onClick={add}
            className="px-4 py-1.5 text-xs font-bold rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#1a4063] disabled:opacity-50"
          >
            {saving ? "Saving…" : (noteType === "call" ? "Log call" : noteType === "meeting" ? "Log meeting" : "Add note")}
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState text="No notes yet — capture screening thoughts, references, red flags." />
      ) : (
        <ul className="space-y-3">
          {items.map((n) => (
            <li key={n.id} className="bg-[#FAFAF7] rounded-xl p-4 flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1.5">
                  {typePill(n.note_type)}
                  <span className="text-[10px] uppercase tracking-widest text-[#6F8FA8]">
                    {n.author || "admin"} · {relTime(n.happened_at || n.created_at)}
                  </span>
                </div>
                <p className="text-sm text-[#0D2F4D] whitespace-pre-line">{n.content}</p>
              </div>
              <button
                data-testid={`candidate-note-delete-${n.id}`}
                onClick={() => remove(n.id)}
                className="text-[#94A3B8] hover:text-red-500"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function CommsPanel({ entityId, entityType, pw, items, onChange }) {
  const [f, setF] = useState({
    channel: "call", direction: "out", subject: "", body: "", contact_person: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.subject.trim() && !f.body.trim()) {
      toast.error("Add a subject or body.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${BASE}/comms`, { entity_type: entityType, entity_id: entityId, ...f }, authHeader(pw));
      setF({ channel: "call", direction: "out", subject: "", body: "", contact_person: "" });
      toast.success("Comms logged.");
      onChange?.();
    } catch { toast.error("Couldn't log comms."); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-4">
      <div className="bg-[#F2F2EF] rounded-xl p-4 space-y-3">
        <div className="grid md:grid-cols-3 gap-3">
          <label className="block text-xs">
            <span className="block text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold mb-1">Channel</span>
            <select
              data-testid="comms-channel"
              value={f.channel}
              onChange={(e) => setF({ ...f, channel: e.target.value })}
              className="w-full rounded-lg border border-[#D6DCE3] bg-white px-3 py-2 text-sm"
            >
              {COMMS_CHANNELS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </label>
          <label className="block text-xs">
            <span className="block text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold mb-1">Direction</span>
            <select
              value={f.direction}
              onChange={(e) => setF({ ...f, direction: e.target.value })}
              className="w-full rounded-lg border border-[#D6DCE3] bg-white px-3 py-2 text-sm"
            >
              <option value="out">Outbound</option>
              <option value="in">Inbound</option>
            </select>
          </label>
          <label className="block text-xs">
            <span className="block text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold mb-1">With</span>
            <input
              value={f.contact_person}
              onChange={(e) => setF({ ...f, contact_person: e.target.value })}
              placeholder="Optional"
              className="w-full rounded-lg border border-[#D6DCE3] bg-white px-3 py-2 text-sm"
            />
          </label>
        </div>
        <input
          data-testid="comms-subject"
          value={f.subject}
          onChange={(e) => setF({ ...f, subject: e.target.value })}
          placeholder="Subject / summary"
          className="w-full rounded-lg border border-[#D6DCE3] bg-white px-3 py-2 text-sm"
        />
        <textarea
          data-testid="comms-body"
          value={f.body}
          onChange={(e) => setF({ ...f, body: e.target.value })}
          rows={3}
          placeholder="What was discussed?"
          className="w-full rounded-lg border border-[#D6DCE3] bg-white px-3 py-2 text-sm resize-y"
        />
        <div className="flex justify-end">
          <button
            data-testid="comms-submit"
            disabled={saving}
            onClick={submit}
            className="px-4 py-1.5 text-xs font-bold rounded-full bg-[#0D2F4D] text-white hover:bg-[#1a4063] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Log comms"}
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState text="No comms logged yet." />
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li key={it.id} className="bg-[#FAFAF7] rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-[#0D2F4D]">
                    {channelMeta(it.channel).label} · {it.direction === "in" ? "Inbound" : "Outbound"}
                    {it.contact_person && ` · with ${it.contact_person}`}
                  </p>
                  {it.subject && <p className="text-sm font-semibold text-[#0D2F4D] mt-1">{it.subject}</p>}
                  {it.body && <p className="text-sm text-[#6F8FA8] mt-1 whitespace-pre-line">{it.body}</p>}
                </div>
                <p className="text-[10px] text-[#94A3B8] whitespace-nowrap">{relTime(it.at)}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function DocumentsPanel({ docs, entityId, entityType, pw, onChange }) {
  const [f, setF] = useState({ name: "", url: "", kind: "cv" });
  const [saving, setSaving] = useState(false);
  const [upKind, setUpKind] = useState("cv");
  const [upFile, setUpFile] = useState(null);
  const [upName, setUpName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const fileRef = useRef(null);

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("entity_type", entityType);
        fd.append("entity_id", entityId);
        fd.append("kind", upKind);
        fd.append("file", file);
        await axios.post(`${BASE}/documents/upload`, fd, { headers: { "X-Admin-Password": pw } });
        ok += 1;
      } catch (e) { fail += 1; }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} file${ok === 1 ? "" : "s"} saved to storage${fail ? ` · ${fail} failed` : ""}.`);
    else toast.error("Upload failed.");
    onChange?.();
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) await uploadFiles(e.dataTransfer.files);
  };

  const upload = async () => {
    if (!upFile) { toast.error("Choose a file first."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("entity_type", entityType);
      fd.append("entity_id", entityId);
      fd.append("kind", upKind);
      if (upName.trim()) fd.append("name", upName.trim());
      fd.append("file", upFile);
      await axios.post(`${BASE}/documents/upload`, fd, { headers: { "X-Admin-Password": pw } });
      toast.success(upKind === "cv" && entityType === "candidate"
        ? "CV uploaded — the candidate's CV link now points to this file."
        : "File uploaded.");
      setUpFile(null);
      setUpName("");
      if (fileRef.current) fileRef.current.value = "";
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const add = async () => {
    if (!f.name.trim() || !f.url.trim()) {
      toast.error("Name and URL required.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${BASE}/documents`, { entity_type: entityType, entity_id: entityId, ...f }, authHeader(pw));
      setF({ name: "", url: "", kind: "cv" });
      toast.success("Document added.");
      onChange?.();
    } catch { toast.error("Couldn't add document."); }
    finally { setSaving(false); }
  };
  const remove = async (docId) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await axios.delete(`${BASE}/documents/${entityType}/${entityId}/${docId}`, authHeader(pw));
      onChange?.();
    } catch { toast.error("Couldn't remove."); }
  };
  const startRename = (d) => { setEditingId(d.id); setEditName(d.name || ""); };
  const saveRename = async (docId) => {
    const name = editName.trim();
    if (!name) { toast.error("Title can't be empty."); return; }
    try {
      await axios.patch(`${BASE}/documents/${entityType}/${entityId}/${docId}`, { name }, authHeader(pw));
      setEditingId(null);
      toast.success("Title updated.");
      onChange?.();
    } catch { toast.error("Couldn't rename document."); }
  };
  const shareDoc = async (d) => {
    try {
      const r = await axios.post(`${BASE}/documents/${entityType}/${entityId}/${d.id}/share`,
        { expires_days: 7 }, authHeader(pw));
      const link = `${process.env.REACT_APP_BACKEND_URL}${r.data.url_path}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("7-day share link copied — paste it into any email.");
      } catch {
        window.prompt("Copy this 7-day share link:", link);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create share link.");
    }
  };

  const docHref = (d, opts = {}) => {
    const dl = opts.download ? "&dl=1" : "";
    if (d.uploaded_file) {
      return `${process.env.REACT_APP_BACKEND_URL}${d.url}?pw=${encodeURIComponent(pw)}${dl}`;
    }
    // Careers-hosted documents (theditchcareers.com.au or the older
    // employer-jobs-3.emergent.host preview host) all sit behind an
    // X-API-Key header that a browser <a href> can't attach — so we
    // route them through our own proxy regardless of whether the doc
    // record was explicitly flagged `ingested`. CVs use the dedicated
    // cv-proxy (nicer filename + rescue caching); everything else
    // (IDs, passports, references) goes through the generic
    // document-proxy which whitelists the URL against this candidate's
    // documents[] before fetching.
    const rawUrl = String(d.url || "");
    const isCareersHost = /(theditchcareers\.com\.au|employer-jobs-3\.emergent\.host)/i.test(rawUrl);
    if (entityType === "candidate" && (d.ingested || d.source === "talent_hub_ingest" || isCareersHost)) {
      const looksLikeCv =
        d.kind === "cv" ||
        d.doc_type === "cv" ||
        /(^|[^a-z])(cv|resume)([^a-z]|$)/i.test(String(d.name || d.filename || d.original_name || "")) ||
        /(cv|resume)\.(pdf|docx?|rtf)$/i.test(String(d.name || d.filename || d.original_name || ""));
      if (looksLikeCv) {
        return `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/candidates/${entityId}/cv-proxy?pw=${encodeURIComponent(pw)}${dl}`;
      }
      return `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/candidates/${entityId}/document-proxy?pw=${encodeURIComponent(pw)}&url=${encodeURIComponent(rawUrl)}${dl}`;
    }
    // Internal download endpoints (our own /api/admin/workspace/documents/…/file
    // — e.g. Careers CVs that were rehosted into Object Storage). A bare <a href>
    // to the relative path hits the API with no auth → 401 "Invalid admin
    // credentials". Prepend the backend origin + the admin password.
    if (rawUrl.startsWith("/api/")) {
      const sep = rawUrl.includes("?") ? "&" : "?";
      return `${process.env.REACT_APP_BACKEND_URL}${rawUrl}${sep}pw=${encodeURIComponent(pw)}${dl}`;
    }
    return d.url;
  };
  const prettySize = (n) => !n ? "" : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

  return (
    <div className="space-y-4">
      <div className="bg-[#0D2F4D] rounded-xl p-4">
        <p className="text-[10px] uppercase tracking-widest font-bold text-[#C6D21F] mb-2">Upload any document (PDF, Word, images, spreadsheets, slides, zip — max 50 MB)</p>
        <div
          data-testid="doc-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={`mb-3 rounded-xl border-2 border-dashed px-4 py-6 text-center cursor-pointer transition-colors ${dragActive ? "border-[#C6D21F] bg-[#C6D21F]/10" : "border-white/25 bg-white/5 hover:border-[#C6D21F]/60"}`}
        >
          <p className="text-sm font-bold text-white">{uploading ? "Uploading…" : "Drag & drop files here"}</p>
          <p className="text-[11px] text-white/50 mt-1">or click to browse · saves straight to secure storage · multiple files OK</p>
        </div>
        <div className="grid md:grid-cols-[1.5fr_1.5fr_150px_auto] gap-2">
          <input
            ref={fileRef}
            type="file"
            data-testid="doc-file"
            onChange={(e) => setUpFile(e.target.files?.[0] || null)}
            className="min-w-0 w-full rounded-lg border border-white/20 bg-white/10 text-white px-3 py-2 text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[#C6D21F] file:text-[#0D2F4D] file:px-3 file:py-1 file:text-xs file:font-bold file:cursor-pointer"
          />
          <input
            data-testid="doc-upload-title"
            value={upName}
            onChange={(e) => setUpName(e.target.value)}
            placeholder="Title / label (optional)"
            className="min-w-0 w-full rounded-lg border border-white/20 bg-white px-3 py-2 text-sm"
          />
          <select
            data-testid="doc-upload-kind"
            value={upKind}
            onChange={(e) => setUpKind(e.target.value)}
            className="min-w-0 w-full rounded-lg border border-white/20 bg-white px-3 py-2 text-sm"
          >
            {DOC_KINDS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
          </select>
          <button
            data-testid="doc-upload"
            disabled={uploading || !upFile}
            onClick={upload}
            className="px-4 py-2 text-xs font-bold rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#d4e02c] disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
        {entityType === "candidate" && (
          <p className="text-[10px] text-white/50 mt-2">Uploading a CV also updates this candidate's CV link automatically.</p>
        )}
      </div>

      <div className="bg-[#F2F2EF] rounded-xl p-4">
        <p className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8] mb-2">Or paste a link (Google Drive / Dropbox)</p>
        <div className="grid md:grid-cols-[1fr_2fr_150px_auto] gap-2">
          <input
            data-testid="doc-name"
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            placeholder="File name"
            className="min-w-0 w-full rounded-lg border border-[#D6DCE3] bg-white px-3 py-2 text-sm"
          />
          <input
            data-testid="doc-url"
            value={f.url}
            onChange={(e) => setF({ ...f, url: e.target.value })}
            placeholder="https://…"
            className="min-w-0 w-full rounded-lg border border-[#D6DCE3] bg-white px-3 py-2 text-sm"
          />
          <select
            data-testid="doc-kind"
            value={f.kind}
            onChange={(e) => setF({ ...f, kind: e.target.value })}
            className="min-w-0 w-full rounded-lg border border-[#D6DCE3] bg-white px-3 py-2 text-sm"
          >
            {DOC_KINDS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
          </select>
          <button
            data-testid="doc-add"
            disabled={saving}
            onClick={add}
            className="px-4 py-2 text-xs font-bold rounded-full bg-[#0D2F4D] text-white hover:bg-[#1a4063] disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Plus size={12} /> Add
          </button>
        </div>
      </div>

      {docs.length === 0 ? (
        <EmptyState text="No documents yet. Upload CVs, cover letters, visa docs, references." />
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => (
            <li key={d.id} className="bg-[#FAFAF7] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 overflow-hidden">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-[#6F8FA8] shrink-0" />
                  {editingId === d.id ? (
                    <span className="inline-flex items-center gap-1.5">
                      <input
                        autoFocus
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(d.id); if (e.key === "Escape") setEditingId(null); }}
                        data-testid={`doc-rename-input-${d.id}`}
                        className="rounded-md border border-[#0D2F4D] px-2 py-1 text-sm w-56"
                      />
                      <button onClick={() => saveRename(d.id)} data-testid={`doc-rename-save-${d.id}`}
                        className="text-[10px] font-bold px-2 py-1 rounded-full bg-[#0D2F4D] text-white">Save</button>
                      <button onClick={() => setEditingId(null)} className="text-[10px] font-bold px-2 py-1 rounded-full border border-[#E4E7EA] text-[#6F8FA8]">Cancel</button>
                    </span>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-[#0D2F4D] truncate">{d.name}</p>
                      <button
                        onClick={() => startRename(d)}
                        data-testid={`doc-rename-${d.id}`}
                        title="Rename / add a title"
                        className="text-[#6F8FA8] hover:text-[#0D2F4D] shrink-0"
                      >
                        <Pencil size={12} />
                      </button>
                    </>
                  )}
                  <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-white text-[#6F8FA8] border border-[#E4E7EA]">
                    {DOC_KINDS.find((k) => k.v === d.kind)?.label || d.kind}
                  </span>
                  {d.uploaded_file && (
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#C6D21F]/20 text-[#5a6410] border border-[#C6D21F]/40">
                      uploaded{d.size ? ` · ${prettySize(d.size)}` : ""}
                    </span>
                  )}
                  {d.ingested && (
                    <span
                      data-testid={`doc-ingested-${d.id}`}
                      title="Auto-imported from the Careers intake form"
                      className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#0D2F4D]/10 text-[#0D2F4D] border border-[#0D2F4D]/20"
                    >
                      from careers
                    </span>
                  )}
                </div>
                <a href={docHref(d)} target="_blank" rel="noreferrer" data-testid={`doc-view-${d.id}`}
                  className="text-xs text-[#6F8FA8] hover:text-[#0D2F4D] break-all inline-flex items-center gap-1 mt-1">
                  {d.uploaded_file ? (d.original_name || d.name) : d.url} <ExternalLink size={10} />
                </a>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0 self-start sm:self-auto">
                <a
                  href={docHref(d)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`doc-view-btn-${d.id}`}
                  title="View in browser"
                  className="text-[10px] font-bold px-3 py-1.5 rounded-full bg-[#0D2F4D] text-white hover:bg-[#1a4063] transition-colors inline-flex items-center gap-1"
                >
                  <Eye size={10} /> View
                </a>
                {d.url && (
                  <a
                    href={docHref(d, { download: true })}
                    data-testid={`doc-download-${d.id}`}
                    title="Download file"
                    className="text-[10px] font-bold px-3 py-1.5 rounded-full border border-[#0D2F4D]/20 text-[#0D2F4D] hover:bg-[#0D2F4D] hover:text-white transition-colors inline-flex items-center gap-1"
                  >
                    <Download size={10} /> Download
                  </a>
                )}
                {d.uploaded_file && (
                  <button
                    onClick={() => shareDoc(d)}
                    data-testid={`doc-share-${d.id}`}
                    title="Copy a 7-day public share link"
                    className="text-[10px] font-bold px-3 py-1.5 rounded-full border border-[#0D2F4D]/20 text-[#0D2F4D] hover:bg-[#0D2F4D] hover:text-white transition-colors inline-flex items-center gap-1"
                  >
                    <ExternalLink size={10} /> Share link
                  </button>
                )}
                {!d.ingested && (
                  <button onClick={() => remove(d.id)} data-testid={`doc-remove-${d.id}`} className="text-[#94A3B8] hover:text-red-500 shrink-0">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ApplicationsPanel({ items, pw, candidateId, onChange }) {
  const unlink = async (appId) => {
    if (!window.confirm("Remove this application?")) return;
    try {
      await axios.delete(`${BASE}/applications/${appId}`, authHeader(pw));
      onChange?.();
    } catch { toast.error("Couldn't remove."); }
  };
  if (!items.length) {
    return <EmptyState text="This candidate hasn't been applied to any role yet. Use the Matching view on a role to shortlist them." />;
  }
  return (
    <ul className="space-y-3">
      {items.map(({ role, company, apps }) => (
        <li key={role.id} className="bg-[#FAFAF7] rounded-xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-[#0D2F4D]">{role.title}</p>
              <p className="text-xs text-[#6F8FA8]">
                {company?.name || "—"} · {role.location || "—"} · {role.status}
              </p>
            </div>
            {apps.map((a) => (
              <button
                key={a.id}
                onClick={() => unlink(a.id)}
                className="text-xs text-[#94A3B8] hover:text-red-500 inline-flex items-center gap-1"
              >
                <X size={10} /> Unlink
              </button>
            ))}
          </div>
        </li>
      ))}
    </ul>
  );
}

/* ─────────────────────────────── Recruiter Notes panel ─────────────────────────────── */
function StarRow({ value, onChange, disabled }) {
  return (
    <div className="inline-flex items-center gap-1" data-testid="candidate-rating-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onClick={() => onChange(n === value ? null : n)}
          data-testid={`candidate-rating-star-${n}`}
          className="disabled:opacity-50"
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star
            size={22}
            className={cx("transition-colors", (value || 0) >= n ? "fill-[#C6D21F] stroke-[#0D2F4D]" : "stroke-[#94A3B8]")}
          />
        </button>
      ))}
      <span className="text-xs text-[#6F8FA8] ml-2">
        {value ? `${value}/5` : "unrated"}
      </span>
    </div>
  );
}

function RecruiterPanel({ candidateId, pw, record, onChange }) {
  const H = useMemo(() => authHeader(pw), [pw]);
  const initial = (record?.candidate?.recruiter_notes) || {
    summary: "",
    interview_notes: "",
    references_done: false,
    references: [],
    overall_rating: null,
    next_action: "",
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [seekSending, setSeekSending] = useState(false);
  const c = record?.candidate || {};
  const p = record?.person || {};
  const alreadySent = !!c.seek_registration_sent_at;

  useEffect(() => {
    setForm(record?.candidate?.recruiter_notes || initial);
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record]);

  const sendSeek = async () => {
    const label = alreadySent ? "Re-send Laura's SEEK registration email?" : "Send Laura's SEEK registration email to this candidate?";
    if (!window.confirm(label)) return;
    setSeekSending(true);
    try {
      const r = await axios.post(
        `${BASE}/candidates/${candidateId}/send-seek-registration`,
        { recruiter_name: "Laura Young" },
        H,
      );
      if (r.data.ok) {
        toast.success(alreadySent ? "SEEK email re-sent." : "SEEK registration email sent.");
      } else {
        toast.error(`Send failed: ${r.data.error || "unknown"}`);
      }
      onChange?.();
    } catch (e) {
      toast.error(`Send failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSeekSending(false);
    }
  };

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const setRef = (i, patch) => {
    const refs = [...(form.references || [])];
    refs[i] = { ...refs[i], ...patch };
    set({ references: refs });
  };
  // Stable id per reference row so React reconciles correctly on remove
  // (index-as-key would collapse input focus onto the wrong row after a
  // deletion in the middle of the list).
  const _newRefId = () => (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `ref-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const addRef = () => set({ references: [...(form.references || []), { _rid: _newRefId(), name: "", company: "", phone: "", notes: "" }] });
  const rmRef = (i) => {
    const refs = [...(form.references || [])];
    refs.splice(i, 1);
    set({ references: refs });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Strip the UI-only _rid key from references so the render key never
      // leaks into the persisted recruiter_notes payload.
      const clean = {
        ...form,
        references: (form.references || []).map(({ _rid, ...rest }) => rest),
      };
      await axios.patch(`${BASE}/candidates/${candidateId}`, { recruiter_notes: clean }, H);
      toast.success("Recruiter notes saved");
      setDirty(false);
      onChange?.();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="recruiter-panel">
      {/* SEEK registration action */}
      <div className="rounded-2xl border border-[#e2dccd] bg-gradient-to-br from-[#F5F3EE] to-[#FCFBF8] p-5" data-testid="seek-registration-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#6F8FA8]">SEEK funnel</span>
              {alreadySent && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#C6D21F]/25 text-[#0D2F4D] text-[10px] font-bold" data-testid="seek-status-inline">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0D2F4D]"/> Sent {relTime(c.seek_registration_sent_at)}
                </span>
              )}
            </div>
            <h3 className="text-lg font-serif italic text-[#0D2F4D] mb-1" style={{ fontFamily: "Georgia, serif" }}>
              Laura's SEEK registration email
            </h3>
            <p className="text-sm text-[#6F8FA8]">
              Sends the standard SEEK-applicant follow-up with the registration link
              (<code className="text-[#0D2F4D]">theditchcareers.com.au/register</code>).
              {p.email && <span> To <b className="text-[#0D2F4D]">{p.email}</b>.</span>}
            </p>
          </div>
          <button
            onClick={sendSeek}
            disabled={seekSending || !p.email}
            data-testid="send-seek-registration"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold bg-[#0D2F4D] text-[#C6D21F] disabled:opacity-40 hover:bg-[#0D2F4D]/90 transition-colors"
          >
            {seekSending ? "Sending…" : (alreadySent ? "Re-send email" : "Send registration email")}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <SectionHeader title="Recruiter notes" />
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="recruiter-save"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-full bg-[#0D2F4D] text-[#C6D21F] disabled:opacity-40 hover:bg-[#0D2F4D]/90 transition-colors"
        >
          <Save size={14}/>{saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">Candidate summary</label>
          <textarea
            value={form.summary || ""}
            onChange={(e) => set({ summary: e.target.value })}
            data-testid="recruiter-summary"
            rows={4}
            className="mt-1.5 w-full border border-[#E4E7EA] rounded-xl p-3 text-sm focus:border-[#0D2F4D] outline-none"
            placeholder="A 2-3 line summary of who this candidate is and where they fit."
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">Interview notes</label>
          <textarea
            value={form.interview_notes || ""}
            onChange={(e) => set({ interview_notes: e.target.value })}
            data-testid="recruiter-interview-notes"
            rows={4}
            className="mt-1.5 w-full border border-[#E4E7EA] rounded-xl p-3 text-sm focus:border-[#0D2F4D] outline-none"
            placeholder="Key takeaways from your call — communication, tech chops, red flags."
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">Overall rating</label>
          <div className="mt-2">
            <StarRow value={form.overall_rating || 0} onChange={(v) => set({ overall_rating: v })} />
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">Next action</label>
          <input
            value={form.next_action || ""}
            onChange={(e) => set({ next_action: e.target.value })}
            data-testid="recruiter-next-action"
            className="mt-1.5 w-full border border-[#E4E7EA] rounded-xl p-2.5 text-sm focus:border-[#0D2F4D] outline-none"
            placeholder="e.g. Send to Melbourne fintech · Book Zoom Fri"
          />
        </div>
      </div>

      {/* References */}
      <div className="border-t border-[#E4E7EA] pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <SectionHeader title="References" />
            <label className="inline-flex items-center gap-2 text-sm text-[#0D2F4D]" data-testid="recruiter-refs-done-toggle">
              <input
                type="checkbox"
                checked={!!form.references_done}
                onChange={(e) => set({ references_done: e.target.checked })}
                className="w-4 h-4 rounded"
                data-testid="recruiter-refs-done"
              />
              References completed
            </label>
          </div>
          <button
            onClick={addRef}
            data-testid="recruiter-refs-add"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full border border-[#0D2F4D] text-[#0D2F4D] hover:bg-[#F2F2EF]"
          >
            <Plus size={14}/>Add reference
          </button>
        </div>

        {(form.references || []).length === 0 ? (
          <EmptyState text="No references logged yet." />
        ) : (
          <div className="space-y-3">
            {form.references.map((r, i) => (
              <div key={r._rid || `legacy-${i}`} className="bg-[#F2F2EF] rounded-xl p-4" data-testid={`recruiter-ref-${i}`}>
                <div className="flex items-start justify-between mb-3">
                  <span className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">Ref #{i + 1}</span>
                  <button
                    onClick={() => rmRef(i)}
                    data-testid={`recruiter-ref-remove-${i}`}
                    className="text-[#94A3B8] hover:text-red-500"
                    aria-label="Remove reference"
                  ><Trash2 size={14}/></button>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <input
                    value={r.name || ""} onChange={(e) => setRef(i, { name: e.target.value })}
                    placeholder="Name" data-testid={`recruiter-ref-name-${i}`}
                    className="border border-[#E4E7EA] rounded-lg p-2 text-sm outline-none focus:border-[#0D2F4D]"
                  />
                  <input
                    value={r.company || ""} onChange={(e) => setRef(i, { company: e.target.value })}
                    placeholder="Company" data-testid={`recruiter-ref-company-${i}`}
                    className="border border-[#E4E7EA] rounded-lg p-2 text-sm outline-none focus:border-[#0D2F4D]"
                  />
                  <input
                    value={r.phone || ""} onChange={(e) => setRef(i, { phone: e.target.value })}
                    placeholder="Phone" data-testid={`recruiter-ref-phone-${i}`}
                    className="border border-[#E4E7EA] rounded-lg p-2 text-sm outline-none focus:border-[#0D2F4D]"
                  />
                </div>
                <textarea
                  value={r.notes || ""} onChange={(e) => setRef(i, { notes: e.target.value })}
                  placeholder="Notes from this reference conversation…"
                  data-testid={`recruiter-ref-notes-${i}`}
                  rows={2}
                  className="mt-3 w-full border border-[#E4E7EA] rounded-lg p-2 text-sm outline-none focus:border-[#0D2F4D]"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#F2F2EF] rounded-xl p-4 text-xs text-[#6F8FA8]">
        <strong className="text-[#0D2F4D]">Heads up:</strong> Moving this candidate to
        <span className="mx-1 font-mono text-[#0D2F4D]">Submitted</span>,
        <span className="mx-1 font-mono text-[#0D2F4D]">Offer</span>, or
        <span className="mx-1 font-mono text-[#0D2F4D]">Placed</span>
        fires a Careers team-alert email via the Email Hub.
      </div>
    </div>
  );
}


/* ─────────────────── Lifecycle status bar (Priya's post-call flag) ─────────────────── */
const LIFECYCLE_OPTIONS = [
  { v: "Active",         color: "#C6D21F", label: "Active" },
  { v: "Keep Warm",      color: "#F59E0B", label: "Keep Warm" },
  { v: "Not Progressing",color: "#94A3B8", label: "Not Progressing" },
];

function LifecycleStatusBar({ candidateId, value, updatedAt, pw, onSaved }) {
  const [busy, setBusy] = useState(false);
  const set = async (next) => {
    // Toggle off if clicking the current one
    const target = value === next ? "" : next;
    setBusy(true);
    try {
      await axios.patch(
        `${BASE}/candidates/${candidateId}`,
        { lifecycle_status: target },
        { headers: { "X-Admin-Password": pw } },
      );
      toast.success(target ? `Set to ${target}.` : "Lifecycle status cleared.");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save status.");
    } finally { setBusy(false); }
  };
  return (
    <div className="p-4 bg-white border-t border-[#F2F2EF]" data-testid="candidate-lifecycle-bar">
      <p className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold mb-2">
        Candidate lifecycle
        <span className="ml-2 font-normal normal-case text-[#94A3B8]">
          (set after each call — separate from application stage)
        </span>
      </p>
      <div className="flex flex-wrap gap-1.5">
        {LIFECYCLE_OPTIONS.map((s) => (
          <button
            key={s.v}
            data-testid={`candidate-lifecycle-${s.v.toLowerCase().replace(/ /g, "-")}`}
            disabled={busy}
            onClick={() => set(s.v)}
            className={cx(
              "px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest border transition-all disabled:opacity-50",
              value === s.v
                ? "text-white shadow-inner"
                : "text-[#6F8FA8] border-[#D6DCE3] hover:text-[#0D2F4D] hover:border-[#0D2F4D] bg-white",
            )}
            style={value === s.v ? { background: s.color, borderColor: s.color } : {}}
            title={value === s.v ? "Click to clear" : `Mark as ${s.label}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {updatedAt && (
        <p className="text-[10px] text-[#6F8FA8] mt-2">Updated {relTime(updatedAt)}</p>
      )}
    </div>
  );
}


/* ─────────────────── Branded Profile Panel ─────────────────── */
// Module-level guard so the auto-extract doesn't re-fire when the panel
// unmounts on tab switch. Tracks candidateIds we've already auto-extracted
// for the lifetime of the page load.
const _autoExtractedIds = new Set();

// Hoisted OUT of BrandedProfilePanel — defining these inside the parent
// re-creates their component identity on every keystroke, which unmounts
// the <textarea>/<input> mid-typing and steals focus. Keeping them at
// module scope means React reconciles them as the same element.
const BrandedField = ({ label, k, rows = 4, hint, action, form, upd }) => (
  <div>
    <label className="flex items-baseline justify-between gap-2 text-[10px] uppercase tracking-[0.14em] font-bold text-[#6F8FA8]">
      <span>{label}</span>
      <div className="flex items-center gap-2">
        {action}
        <span className="normal-case tracking-normal text-[#94A3B8] font-normal text-[10px]">
          {(form[k] || "").length} chars
        </span>
      </div>
    </label>
    {hint && <p className="text-[11px] text-[#94A3B8] mt-1 leading-snug">{hint}</p>}
    <textarea
      rows={rows}
      value={form[k] || ""}
      onChange={(e) => upd(k, e.target.value)}
      data-testid={`branded-${k}`}
      className="w-full mt-1.5 bg-white border border-[#E4E7EA] rounded-lg px-3.5 py-2.5 text-sm text-[#0D2F4D] leading-relaxed focus:outline-none focus:border-[#0D2F4D] focus:ring-4 focus:ring-[#0D2F4D]/5 transition-all resize-y font-[Inter,system-ui,sans-serif]"
      style={{ fontFeatureSettings: '"ss01","cv11"' }}
    />
  </div>
);

const BrandedSnapField = ({ label, k, placeholder, form, upd }) => (
  <div>
    <label className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#6F8FA8]">{label}</label>
    <input
      type="text"
      value={form[k] || ""}
      onChange={(e) => upd(k, e.target.value)}
      data-testid={`snap-${k}`}
      placeholder={placeholder || "—"}
      className="w-full mt-1 bg-white border border-[#E4E7EA] rounded-lg px-2.5 py-1.5 text-xs text-[#0D2F4D] focus:outline-none focus:border-[#0D2F4D] focus:ring-4 focus:ring-[#0D2F4D]/5 transition-all"
    />
  </div>
);

function BrandedProfilePanel({ candidateId, pw, record, onChange }) {
  const c = record?.candidate || {};
  const p = record?.person || {};
  // Photo upload state — recruiter can drop a headshot for the top-right
  // of the branded CV. URL comes back from the /documents/upload endpoint
  // which also persists it on the candidate doc as `branded_photo_url`.
  const [photoUrl, setPhotoUrl] = useState(c.branded_photo_url || "");
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoInputRef = useRef(null);
  useEffect(() => { setPhotoUrl(c.branded_photo_url || ""); }, [candidateId, c.branded_photo_url]);
  const onPickPhoto = async (ev) => {
    const file = ev.target.files?.[0];
    if (ev.target) ev.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpe?g)$/i.test(file.type || "")) {
      toast.error("Please choose a PNG or JPG.");
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Photo must be under 6 MB.");
      return;
    }
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("entity_type", "candidate");
      fd.append("entity_id", candidateId);
      fd.append("kind", "branded_photo");
      fd.append("name", file.name || "cv-photo");
      fd.append("file", file);
      const r = await axios.post(`${BASE}/documents/upload`, fd, {
        headers: { "X-Admin-Password": pw, "Content-Type": "multipart/form-data" },
      });
      const u = r?.data?.document?.url;
      if (u) {
        setPhotoUrl(u);
        toast.success("Photo added to CV.");
        onChange?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed.");
    } finally {
      setPhotoBusy(false);
    }
  };
  const removePhoto = async () => {
    if (!window.confirm("Remove the photo from this branded CV?")) return;
    setPhotoBusy(true);
    try {
      await axios.delete(`${BASE}/candidates/${candidateId}/branded-photo`,
        { headers: { "X-Admin-Password": pw } });
      setPhotoUrl("");
      toast.success("Photo removed.");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove photo.");
    } finally {
      setPhotoBusy(false);
    }
  };
  // Seed the editor: prefer branded_* if already saved, otherwise fall back
  // to source fields (never phone / email — those stay in the cover email).
  const seed = () => ({
    branded_headline:   c.branded_headline   || [
                          `${p.first_name || ""} ${p.last_name || ""}`.trim(),
                          [c.role_title, c.industry, c.candidate_type === "relocating" ? "Relocating to AUS" : ""]
                            .filter(Boolean).join("  |  "),
                          [c.location_target || c.current_location, c.availability && `Available from ${c.availability}`]
                            .filter(Boolean).join("  •  "),
                        ].filter(Boolean).join("\n"),
    branded_summary:    c.branded_summary    || c.recruiter_summary || toStr(c.summary),
    branded_experience: toStr(c.branded_experience || c.work_history),
    branded_skills:     c.branded_skills     ||
                          (Array.isArray(c.skills) ? c.skills.join("\n") : toStr(c.skills)),
    cover_email_subject: c.cover_email_subject || "",
    cover_email_body:    c.cover_email_body    || "",
    // Snapshot sidebar — recruiter must be able to click and type into
    // every field before hitting Generate. Empty values are hidden from
    // the final PDF (label + value both suppressed).
    current_location:         toStr(c.current_location         || c.location_now),
    availability:             toStr(c.availability             || c.start_date),
    salary_expectation:       toStr(c.salary_expectation),
    role_title:               toStr(c.role_title               || c.role_focus),
    preferred_work_type:      toStr(c.preferred_work_type),
    drivers_licence:          toStr(c.drivers_licence),
    real_estate_registration: toStr(c.real_estate_registration),
    police_clearance:         toStr(c.police_clearance),
    languages:                Array.isArray(c.languages) ? c.languages.join(", ") : toStr(c.languages),
  });
  const [form, setForm] = useState(seed());
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [extractBusy, setExtractBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const [coverAiBusy, setCoverAiBusy] = useState(false);
  const previewSeqRef = useRef(0);

  useEffect(() => { setForm(seed()); setDirty(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [candidateId]);

  // Debounced live PDF preview. Every keystroke schedules a rebuild 550ms
  // later — enough to feel instant but sparse enough that the FastAPI
  // stamper never queues on itself. Sequence guard prevents an older
  // preview overwriting a newer one when the user types fast.
  useEffect(() => {
    const seq = ++previewSeqRef.current;
    setPreviewBusy(true);
    setPreviewErr(null);
    const t = setTimeout(async () => {
      try {
        const r = await axios.post(
          `${BASE}/candidates/${candidateId}/branded-profile-preview.pdf?pw=${encodeURIComponent(pw)}`,
          form,
          { responseType: "blob", headers: { "Content-Type": "application/json" } },
        );
        if (seq !== previewSeqRef.current) return;   // stale
        const url = URL.createObjectURL(r.data);
        // Revoke the previous blob URL so we don't leak memory across edits.
        setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      } catch (e) {
        if (seq !== previewSeqRef.current) return;
        setPreviewErr(e?.response?.data?.detail || "Preview build failed.");
      } finally {
        if (seq === previewSeqRef.current) setPreviewBusy(false);
      }
    }, 550);
    return () => clearTimeout(t);
  }, [candidateId, pw, form.branded_headline, form.branded_summary, form.branded_experience, form.branded_skills]);

  // Clean up the last blob URL when leaving the tab / unmounting.
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const upd = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  // Full AI extraction — reads the raw CV via Claude and returns all 4
  // branded fields. Used both for auto-fill on first open (with
  // persist:true so the draft survives tab switches) and for the manual
  // "Re-extract from CV" button (persist:false — the recruiter reviews
  // before saving).
  const runExtraction = async ({ silent = false, persist = false } = {}) => {
    setExtractBusy(true);
    try {
      const r = await axios.post(
        `${BASE}/candidates/${candidateId}/branded-profile-extract`,
        {},
        { headers: { "X-Admin-Password": pw } },
      );
      const merged = {
        branded_headline:   r.data.headline   || form.branded_headline,
        branded_summary:    r.data.summary    || form.branded_summary,
        branded_experience: r.data.experience || form.branded_experience,
        branded_skills:     r.data.skills     || form.branded_skills,
      };
      setForm(merged);
      if (persist) {
        // Persist immediately so a tab-switch remount re-seeds the editor
        // from the record and the recruiter's initial draft isn't lost.
        try {
          await axios.patch(`${BASE}/candidates/${candidateId}`,
            { ...merged, branded_ai_draft: true },
            { headers: { "X-Admin-Password": pw } });
          setDirty(false);
          onChange?.();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("[branded] auto-persist failed", e);
          setDirty(true);
        }
      } else {
        setDirty(true);
      }
      if (!silent) toast.success("AI extraction complete — review and edit before saving.");
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Couldn't extract from CV.");
    } finally { setExtractBusy(false); }
  };

  // Auto-fill on first open when NO branded_* field is persisted on the
  // candidate record. Uses the raw candidate document (not the derived
  // form, which synthesises a headline from name/role/etc and would never
  // be empty). Guarded at MODULE level so tab-switching (which unmounts
  // the panel) doesn't re-fire the Claude call.
  useEffect(() => {
    if (_autoExtractedIds.has(candidateId)) return;
    // Wait until the candidate record is actually loaded — otherwise a
    // null/empty c would permanently mark this id as auto-extracted before
    // we ever had a chance to inspect it.
    if (!record || !c || !c.id) return;
    const hasPersisted = !!(c.branded_headline || c.branded_summary ||
                            c.branded_experience || c.branded_skills);
    if (hasPersisted) { _autoExtractedIds.add(candidateId); return; }
    // Only fire when there's a CV to read from — includes cv_url ingested
    // from Careers site via mergeCvIntoDocs().
    const hasCv = mergeCvIntoDocs(c).some((d) => {
      const t = String(d?.doc_type || d?.kind || "").toLowerCase();
      const n = String(d?.name || d?.filename || "").toLowerCase();
      return t === "cv" || t === "resume" || n.includes("cv") || n.includes("resume");
    });
    if (!hasCv) return;
    _autoExtractedIds.add(candidateId);
    runExtraction({ silent: true, persist: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, record]);

  const save = async () => {
    setSaving(true);
    try {
      // Every manual Save is a recruiter approval — clear the AI-draft flag
      // so the badge disappears and downstream flows treat the copy as
      // human-reviewed.
      await axios.patch(`${BASE}/candidates/${candidateId}`,
        { ...form, branded_ai_draft: false },
        { headers: { "X-Admin-Password": pw } });
      toast.success("Branded profile saved.");
      setDirty(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };

  // Cover email — template fill-in. Deterministic + instant. Replaces
  // whatever's in the two cover_email_* fields with the new draft, then
  // persists silently so tab-switches don't wipe it.
  const generateCoverTemplate = async () => {
    if ((form.cover_email_subject || form.cover_email_body) && !window.confirm(
      "Replace the current cover email with a fresh template draft?",
    )) return;
    setCoverBusy(true);
    try {
      const r = await axios.post(
        `${BASE}/candidates/${candidateId}/cover-email-draft`, {},
        { headers: { "X-Admin-Password": pw } },
      );
      setForm((f) => ({ ...f, cover_email_subject: r.data.subject, cover_email_body: r.data.body }));
      await axios.patch(`${BASE}/candidates/${candidateId}`,
        { cover_email_subject: r.data.subject, cover_email_body: r.data.body },
        { headers: { "X-Admin-Password": pw } });
      toast.success("Cover email drafted from record.");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't draft cover email.");
    } finally { setCoverBusy(false); }
  };

  // Cover email — Claude-drafted variant, three-para body in Ditch voice.
  const suggestCoverWithAI = async () => {
    if ((form.cover_email_subject || form.cover_email_body) && !window.confirm(
      "Replace the current cover email with an AI draft?",
    )) return;
    setCoverAiBusy(true);
    try {
      const r = await axios.post(
        `${BASE}/candidates/${candidateId}/cover-email-suggest`, {},
        { headers: { "X-Admin-Password": pw } },
      );
      setForm((f) => ({ ...f, cover_email_subject: r.data.subject, cover_email_body: r.data.body }));
      await axios.patch(`${BASE}/candidates/${candidateId}`,
        { cover_email_subject: r.data.subject, cover_email_body: r.data.body },
        { headers: { "X-Admin-Password": pw } });
      toast.success("AI cover email ready — edit before you send.");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't draft with AI.");
    } finally { setCoverAiBusy(false); }
  };

  const copyCoverToClipboard = async () => {
    const subj = form.cover_email_subject || "";
    const body = form.cover_email_body || "";
    if (!subj && !body) {
      toast.error("Nothing to copy — draft a cover email first.");
      return;
    }
    const text = subj ? `Subject: ${subj}\n\n${body}` : body;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard — paste into Gmail.");
    } catch {
      toast.error("Couldn't access the clipboard.");
    }
  };

  // Auto-Generate Content — Claude fills ONLY the blank branded_* fields.
  // Preserves whatever the recruiter has already typed. Uses the "clean
  // & fill" pattern: no overwrite of manual edits, empty sections stay
  // hidden from the PDF, no orphan headers.
  const autoGenerateMissing = async () => {
    const empties = ["branded_headline","branded_summary","branded_experience","branded_skills"]
      .filter((k) => !(form[k] || "").trim());
    if (empties.length === 0) {
      toast.success("Every branded section is already filled — nothing to auto-generate.");
      return;
    }
    setExtractBusy(true);
    try {
      const r = await axios.post(
        `${BASE}/candidates/${candidateId}/branded-profile-auto-fill`, {},
        { headers: { "X-Admin-Password": pw } },
      );
      const cand = r.data.candidate || {};
      setForm((f) => ({
        ...f,
        branded_headline:   f.branded_headline   || cand.branded_headline   || "",
        branded_summary:    f.branded_summary    || cand.branded_summary    || "",
        branded_experience: f.branded_experience || cand.branded_experience || "",
        branded_skills:     f.branded_skills     || cand.branded_skills     || "",
      }));
      const filled = (r.data.filled || []).length;
      toast.success(filled ? `Auto-filled ${filled} missing section${filled === 1 ? "" : "s"}.`
                           : "AI returned nothing for the missing sections.");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-generate failed.");
    } finally { setExtractBusy(false); }
  };

  // One-click approval that clears the AI-draft flag without changing any
  // branded_* text — for the recruiter who reads the auto-draft and is
  // happy to ship it as-is.
  const approveDraft = async () => {
    setSaving(true);
    try {
      await axios.patch(`${BASE}/candidates/${candidateId}`,
        { branded_ai_draft: false },
        { headers: { "X-Admin-Password": pw } });
      toast.success("Draft approved — badge cleared.");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't approve draft.");
    } finally { setSaving(false); }
  };

  const download = async () => {
    if (dirty) { await save(); }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace/candidates/${candidateId}/branded-profile.pdf?pw=${encodeURIComponent(pw)}`;
    window.open(url, "_blank", "noopener");
  };

  // Ask Claude to draft a polished 3-sentence recruiter summary from the
  // candidate's raw CV + record. Puts the draft straight into the textarea
  // so Priya can hand-tune before saving. Doesn't touch the DB.
  const suggestSummary = async () => {
    if (form.branded_summary && !window.confirm(
      "Replace the current summary with an AI draft?\n\n(You can still edit the result before saving.)",
    )) return;
    setAiBusy(true);
    try {
      const r = await axios.post(
        `${BASE}/candidates/${candidateId}/branded-profile-suggest-summary`,
        {},
        { headers: { "X-Admin-Password": pw } },
      );
      upd("branded_summary", r.data.summary);
      toast.success("AI draft ready — edit as needed, then save.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't draft summary.");
    } finally { setAiBusy(false); }
  };

  return (
    <div className="grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-6 -mx-4 -my-4" data-testid="branded-profile-panel">
      {/* ── LEFT — Editor ───────────────────────────────── */}
      <div className="bg-white border-r border-[#EDEEF0] px-6 py-6 space-y-5 overflow-y-auto max-h-[calc(100vh-220px)]">
        <div>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <div className="h-1.5 w-1.5 rounded-full bg-[#C6D21F]" />
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-[#0D2F4D]">Branded profile editor</p>
            {c.branded_ai_draft === true && (
              <span
                data-testid="branded-ai-draft-badge"
                title="Auto-generated by Claude — review, edit or approve before sending to employers"
                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.16em] font-bold px-2 py-[3px] rounded-full bg-amber-50 border border-amber-300 text-amber-800"
              >
                <Sparkles size={9} /> AI draft — review
              </span>
            )}
          </div>
          <p className="text-xs text-[#6F8FA8] leading-relaxed">
            Phone + email stay off the document — they belong in the cover email. The preview updates
            live as you type so what you see is what employers get.
          </p>
        </div>
        <BrandedField form={form} upd={upd} label="Headline block" k="branded_headline" rows={3}
          hint="Line 1 = full name · Line 2 = role focuses (separated with |) · Line 3 = location & availability" />

        {/* ── Recruiter photo — appears top-right of the branded CV headline card. */}
        <div className="border border-[#E4E7EA] rounded-lg p-3" data-testid="branded-photo-block">
          <div className="flex items-start gap-3">
            <div className="w-16 h-16 rounded-md border border-[#E4E7EA] bg-[#F8F9FA] flex-shrink-0 overflow-hidden flex items-center justify-center">
              {photoUrl ? (
                <img
                  src={`${photoUrl}?pw=${encodeURIComponent(pw)}`}
                  alt="CV photo"
                  className="w-full h-full object-cover"
                  data-testid="branded-photo-preview"
                />
              ) : (
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#94A3B8] text-center">No photo</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6F8FA8]">Photo on CV</label>
              <p className="text-[11px] text-[#94A3B8] mt-1 leading-snug">
                Optional headshot for the branded PDF. Renders top-right of the headline card. PNG or JPG, up to 6 MB.
              </p>
              <div className="flex items-center gap-2 mt-2">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={onPickPhoto}
                  className="hidden"
                  data-testid="branded-photo-input"
                />
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={photoBusy}
                  data-testid="branded-photo-upload"
                  className="text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full border border-[#0D2F4D] text-[#0D2F4D] hover:bg-[#0D2F4D] hover:text-[#C6D21F] disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {photoBusy ? "Uploading…" : (photoUrl ? "Replace" : "Upload photo")}
                </button>
                {photoUrl && !photoBusy && (
                  <button
                    type="button"
                    onClick={removePhoto}
                    data-testid="branded-photo-remove"
                    className="text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full border border-[#F5C6C6] text-[#B02A2A] hover:bg-[#FDECEC]"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Snapshot sidebar — every field editable. Blanks are hidden from the PDF. */}
        <details className="border border-[#E4E7EA] rounded-lg" open={false}>
          <summary className="cursor-pointer list-none px-3 py-2 text-[10px] uppercase tracking-[0.14em] font-bold text-[#6F8FA8] flex items-center justify-between">
            <span>Candidate snapshot (10 fields)</span>
            <span className="text-[9px] text-[#94A3B8] normal-case tracking-normal">click to edit · blanks hidden</span>
          </summary>
          <div className="p-3 pt-1 grid grid-cols-2 gap-2.5" data-testid="branded-snapshot-editor">
            <BrandedSnapField form={form} upd={upd} label="Location" k="current_location" placeholder="Sydney, NSW" />
            <BrandedSnapField form={form} upd={upd} label="Availability" k="availability" placeholder="March 2026" />
            <BrandedSnapField form={form} upd={upd} label="Salary expectation" k="salary_expectation" placeholder="AU$140,000 + super" />
            <BrandedSnapField form={form} upd={upd} label="Ideal role(s)" k="role_title" placeholder="Engineering Manager" />
            <BrandedSnapField form={form} upd={upd} label="Preferred work type" k="preferred_work_type" placeholder="Full-time, Permanent" />
            <BrandedSnapField form={form} upd={upd} label="Driver's licence" k="drivers_licence" placeholder="Full — car available" />
            <BrandedSnapField form={form} upd={upd} label="Real estate registration" k="real_estate_registration" placeholder="—" />
            <BrandedSnapField form={form} upd={upd} label="Police clearance" k="police_clearance" placeholder="Cleared 2025" />
            <BrandedSnapField form={form} upd={upd} label="Languages" k="languages" placeholder="English, Te Reo Māori" />
          </div>
        </details>

        <BrandedField form={form} upd={upd} label="Recruiter summary" k="branded_summary" rows={5}
          hint="2–4 sentences in third person — renders in PROFESSIONAL PROFILE."
          action={
            <button
              onClick={suggestSummary}
              disabled={aiBusy}
              data-testid="branded-ai-suggest"
              title="Draft a polished 3-sentence summary from the raw CV using Claude"
              className={`text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 transition-all ${
                aiBusy
                  ? "bg-[#C6D21F]/40 text-[#0D2F4D] cursor-wait"
                  : "bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#1a4063] shadow-sm hover:shadow"
              }`}
            >
              <Sparkles size={11} className={aiBusy ? "animate-pulse" : ""} />
              {aiBusy ? "Drafting…" : "Suggest with AI"}
            </button>
          }
        />
        <BrandedField form={form} upd={upd} label="Key strengths" k="branded_skills" rows={5}
          hint="One per line — first 8 render on the branded PDF as bullets." />
        <BrandedField form={form} upd={upd} label="Professional experience" k="branded_experience" rows={14}
          hint="Free text. Roles + bullets. Overflows onto page 2 automatically." />
        <div className="flex gap-2 flex-wrap pt-2 border-t border-[#F2F2EF]">
          <button
            onClick={autoGenerateMissing}
            disabled={extractBusy}
            data-testid="branded-auto-generate"
            title="Ask Claude to fill any BLANK branded sections — never overwrites your edits"
            className={`text-[11px] font-bold uppercase tracking-[0.14em] px-4 py-2.5 rounded-full inline-flex items-center gap-2 transition-all ${
              extractBusy
                ? "bg-[#C6D21F]/40 text-[#0D2F4D] cursor-wait"
                : "bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#d3df34] shadow-sm hover:shadow"
            }`}
          >
            <Sparkles size={12} className={extractBusy ? "animate-pulse" : ""} />
            {extractBusy ? "Filling…" : "Auto-Generate Content"}
          </button>
          <button
            onClick={() => runExtraction({ silent: false })}
            disabled={extractBusy}
            data-testid="branded-ai-extract"
            title="Re-extract EVERY field from the raw CV using Claude (overwrites current text)"
            className={`text-[11px] font-bold uppercase tracking-[0.14em] px-4 py-2.5 rounded-full inline-flex items-center gap-2 transition-all ${
              extractBusy
                ? "bg-[#C6D21F]/40 text-[#0D2F4D] cursor-wait"
                : "bg-white border border-[#0D2F4D] text-[#0D2F4D] hover:bg-[#0D2F4D] hover:text-[#C6D21F]"
            }`}
          >
            <Sparkles size={12} className={extractBusy ? "animate-pulse" : ""} />
            {extractBusy ? "Extracting…" : "Re-extract from CV"}
          </button>
          <button
            onClick={save}
            disabled={saving || !dirty}
            data-testid="branded-save"
            className="text-[11px] font-bold uppercase tracking-[0.14em] px-4 py-2.5 rounded-full bg-white border border-[#D6DCE3] text-[#0D2F4D] hover:border-[#0D2F4D] disabled:opacity-40 transition-all"
          >
            {saving ? "Saving…" : dirty ? "Save edits" : "Saved ✓"}
          </button>
          {c.branded_ai_draft === true && !dirty && (
            <button
              onClick={approveDraft}
              disabled={saving}
              data-testid="branded-approve-draft"
              title="Mark this draft as recruiter-approved and clear the AI badge"
              className="text-[11px] font-bold uppercase tracking-[0.14em] px-4 py-2.5 rounded-full bg-amber-100 border border-amber-300 text-amber-900 hover:bg-amber-200 disabled:opacity-40 inline-flex items-center gap-2 transition-all"
            >
              <CheckCircle2 size={12} /> Approve draft
            </button>
          )}
          <button
            onClick={download}
            disabled={saving}
            data-testid="branded-generate"
            className="text-[11px] font-bold uppercase tracking-[0.14em] px-4 py-2.5 rounded-full bg-[#0D2F4D] text-white hover:bg-[#1a4063] disabled:opacity-40 inline-flex items-center gap-2 transition-all shadow-sm hover:shadow"
          >
            <FileText size={12} /> Download PDF
          </button>
        </div>

        {/* ── Cover email drafter ────────────────────────── */}
        <div className="pt-5 mt-2 border-t border-[#F2F2EF] space-y-3" data-testid="cover-email-panel">
          <div className="flex items-baseline justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 rounded-full bg-[#0D2F4D]" />
              <p className="text-[10px] uppercase tracking-[0.22em] font-black text-[#0D2F4D]">Cover email to employer</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={generateCoverTemplate}
                disabled={coverBusy || coverAiBusy}
                data-testid="cover-email-template"
                title="Fill in a fast, no-AI draft from the candidate record"
                className={`text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 transition-all ${
                  coverBusy ? "bg-[#C6D21F]/40 text-[#0D2F4D] cursor-wait"
                            : "bg-white border border-[#0D2F4D] text-[#0D2F4D] hover:bg-[#0D2F4D] hover:text-[#C6D21F]"
                }`}
              >
                <Mail size={11} />
                {coverBusy ? "Drafting…" : "Template"}
              </button>
              <button
                onClick={suggestCoverWithAI}
                disabled={coverBusy || coverAiBusy}
                data-testid="cover-email-ai"
                title="Draft a bespoke intro with Claude (Ditch voice)"
                className={`text-[10px] font-bold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 transition-all ${
                  coverAiBusy ? "bg-[#C6D21F]/40 text-[#0D2F4D] cursor-wait"
                              : "bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#1a4063] shadow-sm hover:shadow"
                }`}
              >
                <Sparkles size={11} className={coverAiBusy ? "animate-pulse" : ""} />
                {coverAiBusy ? "Drafting…" : "Suggest with AI"}
              </button>
            </div>
          </div>
          <p className="text-[11px] text-[#94A3B8] leading-snug">
            Auto-drafts the "please find attached" intro so you don't retype the same email. Edit as needed, then copy into Gmail.
          </p>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6F8FA8]">Subject</label>
            <input
              type="text"
              value={form.cover_email_subject || ""}
              onChange={(e) => upd("cover_email_subject", e.target.value)}
              data-testid="cover-email-subject"
              placeholder="e.g. Rami Issa — Engineering Manager | Relocating to Sydney"
              className="w-full mt-1.5 bg-white border border-[#E4E7EA] rounded-lg px-3.5 py-2 text-sm text-[#0D2F4D] focus:outline-none focus:border-[#0D2F4D] focus:ring-4 focus:ring-[#0D2F4D]/5 transition-all"
            />
          </div>
          <div>
            <label className="flex items-baseline justify-between text-[10px] uppercase tracking-[0.14em] font-bold text-[#6F8FA8]">
              <span>Body</span>
              <span className="normal-case tracking-normal text-[#94A3B8] font-normal text-[10px]">{(form.cover_email_body || "").length} chars</span>
            </label>
            <textarea
              rows={8}
              value={form.cover_email_body || ""}
              onChange={(e) => upd("cover_email_body", e.target.value)}
              data-testid="cover-email-body"
              placeholder="Hi there,&#10;&#10;Please find attached the branded profile for…"
              className="w-full mt-1.5 bg-white border border-[#E4E7EA] rounded-lg px-3.5 py-2.5 text-sm text-[#0D2F4D] leading-relaxed focus:outline-none focus:border-[#0D2F4D] focus:ring-4 focus:ring-[#0D2F4D]/5 transition-all resize-y"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={copyCoverToClipboard}
              disabled={coverBusy || coverAiBusy}
              data-testid="cover-email-copy"
              className="text-[11px] font-bold uppercase tracking-[0.14em] px-4 py-2 rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#d3df34] inline-flex items-center gap-2 disabled:opacity-40 transition-all shadow-sm hover:shadow"
            >
              <FileText size={12} /> Copy to clipboard
            </button>
          </div>
        </div>
      </div>

      {/* ── RIGHT — Live preview ────────────────────────── */}
      <div className="bg-[#F5F3EE] p-6 flex flex-col gap-3 max-h-[calc(100vh-220px)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`h-1.5 w-1.5 rounded-full transition-colors ${previewBusy ? "bg-[#F59E0B] animate-pulse" : previewErr ? "bg-red-500" : "bg-[#C6D21F]"}`} />
            <p className="text-[10px] uppercase tracking-[0.22em] font-black text-[#0D2F4D]">
              Live preview
            </p>
          </div>
          <p className="text-[10px] text-[#6F8FA8]">
            {previewBusy ? "Restamping…" : previewErr ? "Preview error" : "Up to date"}
          </p>
        </div>
        <div className="flex-1 bg-white border border-[#E4E7EA] rounded-lg shadow-[0_20px_60px_-30px_rgba(13,47,77,0.35)] overflow-hidden relative">
          {previewErr && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/95 text-center px-6 z-10">
              <div>
                <p className="font-display text-lg font-bold text-[#B91C1C]">Couldn't build preview</p>
                <p className="text-xs text-[#6F8FA8] mt-1">{previewErr}</p>
              </div>
            </div>
          )}
          {previewUrl ? (
            <iframe
              key={previewUrl}
              src={previewUrl + "#toolbar=0&navpanes=0&view=FitH"}
              title="Branded profile preview"
              data-testid="branded-preview-iframe"
              className="w-full h-full"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#94A3B8] text-sm">
              Building preview…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

